// courseContent.ts (enriched version)
import { Module } from "../types";

export const courseModules: Module[] = [
  {
    id: "intro",
    title: "1. Introduction",
    summary:
      "Learn what Postman is, understand REST APIs, and get comfortable with the Postman interface.",
    lessons: [
      {
        id: "what-is-postman",
        title: "What is Postman? A Complete Overview",
        estimatedMinutes: 10,
        content: `
# What is Postman? The Complete API Development Platform

Postman is more than just an API client—it's a comprehensive API development environment used by over 20 million developers worldwide. Founded in 2014, Postman has become the industry standard for API testing, documentation, and collaboration.

## Core Features of Postman

### 1. **API Client** 🔌
The heart of Postman—send HTTP requests and view responses in an intuitive interface. Supports all HTTP methods (GET, POST, PUT, PATCH, DELETE, HEAD, OPTIONS) and various authentication mechanisms.

### 2. **Collections** 📁
Organize related API requests into folders and subfolders. Collections can be exported, shared, version-controlled, and even documented automatically.

### 3. **Environments** 🌍
Manage different configurations for development, staging, and production environments. Use variables to switch between them instantly.

### 4. **Automated Testing** ✓
Write tests in JavaScript that run automatically after each request. Validate status codes, response times, data structures, and business logic.

### 5. **Collection Runner** 🏃
Execute an entire collection or a selected set of requests in sequence. Ideal for regression testing and CI/CD pipelines.

### 6. **Mock Servers** 🎭
Simulate API endpoints before the backend is ready. Define example responses and test your frontend integration early.

### 7. **Monitors** 📊
Schedule collections to run at regular intervals (e.g., every hour). Receive alerts via email or Slack when tests fail or response times exceed thresholds.

### 8. **Documentation** 📚
Generate beautiful, interactive API documentation directly from your collections. Share with stakeholders, external partners, or publish to the web.

### 9. **Workspaces** 👥
Collaborate with team members in shared spaces. Track changes, leave comments, and maintain version history.

### 10. **Postman API** 🔗
Programmatically manage your Postman resources—create collections, run monitors, generate documentation, and more via REST API.

## Use Cases for Postman

| Use Case | Description |
| :--- | :--- |
| **Manual Testing** | Quickly test endpoints during development |
| **Automated Testing** | Run regression tests in CI/CD pipelines |
| **API Exploration** | Understand third-party APIs before integration |
| **Documentation** | Generate API docs without writing a single line |
| **Load Testing** | Simulate multiple concurrent users (Pro feature) |
| **Prototyping** | Design and mock APIs before backend implementation |

<div style="padding: 15px; background-color: #F0FDF4; border-left: 4px solid #22C55E; margin: 20px 0; border-radius: 4px;">
  <strong>💡 Pro Tip:</strong> Postman is free for individual developers! The free tier includes unlimited requests, collections, and environments—perfect for learning.
</div>

## What You'll Build in This Course

By the end of this course, you'll be able to:
- ✅ Send any HTTP request confidently
- ✅ Create organized collections for your APIs
- ✅ Write automated tests to validate API responses
- ✅ Use variables to manage environments
- ✅ Generate professional API documentation
- ✅ Integrate Postman with CI/CD pipelines

Let's get started! 🚀
        `,
        quiz: [
          {
            question:
              "Which Postman feature allows you to simulate API responses before the backend is ready?",
            options: [
              "Collections",
              "Mock Servers",
              "Environments",
              "Monitors",
            ],
            correctAnswer: 1,
            explanation:
              "Mock servers let you create simulated API endpoints with example responses, enabling frontend development to proceed without the actual backend.",
          },
        ],
      },
      {
        id: "http-methods",
        title: "Understanding HTTP Methods Deep Dive",
        estimatedMinutes: 15,
        content: `
# Understanding HTTP Methods: A Complete Reference

HTTP methods (also called HTTP verbs) define the action you want to perform on a resource. This is the foundation of RESTful API design.

## Complete HTTP Methods Reference

| Method | Purpose | Request Body | Response Body | Safe | Idempotent | Cacheable |
| :--- | :--- | :--- | :--- | :--- | :--- | :--- |
| **GET** | Retrieve a resource | ❌ No | ✅ Yes | ✅ Yes | ✅ Yes | ✅ Yes |
| **POST** | Create a new resource | ✅ Yes | ✅ Yes (usually) | ❌ No | ❌ No | ⚠️ Sometimes |
| **PUT** | Replace entire resource | ✅ Yes | ⚠️ Optional | ❌ No | ✅ Yes | ❌ No |
| **PATCH** | Partial update | ✅ Yes | ⚠️ Optional | ❌ No | ❌ No | ❌ No |
| **DELETE** | Remove a resource | ⚠️ Rarely | ⚠️ Optional | ❌ No | ✅ Yes | ❌ No |
| **HEAD** | Get headers only | ❌ No | ❌ No (headers only) | ✅ Yes | ✅ Yes | ✅ Yes |
| **OPTIONS** | Get allowed methods | ❌ No | ✅ Yes (methods list) | ✅ Yes | ✅ Yes | ❌ No |

## Detailed Breakdown of Each Method

### GET - Safe Retrieval
\`GET\` requests should NEVER modify server state. They are read-only operations.

**Example:** Fetching a user profile
\`\`\`http
GET /api/users/123 HTTP/1.1
Host: example.com
Accept: application/json
\`\`\`

**Best Practices:**
- Never include sensitive data in URLs (they appear in server logs)
- Use query parameters for filtering, pagination, and sorting
- Cache GET responses when appropriate to improve performance

### POST - Resource Creation
\`POST\` requests create new subordinate resources. Each POST should create a uniquely identified resource.

**Example:** Creating a new blog post
\`\`\`http
POST /api/posts HTTP/1.1
Host: example.com
Content-Type: application/json

{
  "title": "My First Post",
  "content": "Hello World!",
  "authorId": 456
}
\`\`\`

**Response:** Should return 201 Created with Location header pointing to the new resource.

### PUT - Complete Replacement
\`PUT\` replaces the entire resource at a specific URL. If the resource doesn't exist, some APIs create it.

**Example:** Replacing a user profile completely
\`\`\`http
PUT /api/users/123 HTTP/1.1
Host: example.com
Content-Type: application/json

{
  "name": "John Doe",
  "email": "john@example.com",
  "age": 30
}
\`\`\`

**Important:** Omitting a field in PUT typically means setting it to null/default, not leaving it unchanged.

### PATCH - Partial Update
\`PATCH\` applies partial modifications to a resource.

**Example:** Updating only the email field
\`\`\`http
PATCH /api/users/123 HTTP/1.1
Host: example.com
Content-Type: application/json

{
  "email": "newemail@example.com"
}
\`\`\`

### DELETE - Resource Removal
\`DELETE\` removes the specified resource.

**Example:** Deleting a product
\`\`\`http
DELETE /api/products/789 HTTP/1.1
Host: example.com
\`\`\`

## Understanding Key Concepts

### What is Idempotency? 🔄
An idempotent operation produces the same result whether you execute it once or multiple times.

**Idempotent Examples:**
- GET /users/123 (always returns the same user)
- PUT /users/123 with same data (second update changes nothing)
- DELETE /users/123 (second deletion finds no user to delete)

**Non-Idempotent Examples:**
- POST /users (each request creates a NEW user)
- PATCH /users/123/increment-views (each increment changes the value)

### What are Safe Methods? 🛡️
Safe methods don't modify server state. They're read-only and can be cached aggressively.

**Safe methods:** GET, HEAD, OPTIONS

### Real-World Example: Blog API

| Action | Method | Endpoint | Idempotent? |
| :--- | :--- | :--- | :--- |
| View all posts | GET | /posts | ✅ Yes |
| View single post | GET | /posts/1 | ✅ Yes |
| Create new post | POST | /posts | ❌ No |
| Replace entire post | PUT | /posts/1 | ✅ Yes |
| Update title only | PATCH | /posts/1 | ❌ No |
| Delete post | DELETE | /posts/1 | ✅ Yes |

<div style="padding: 15px; background-color: #FEFCE8; border-left: 4px solid #EAB308; margin: 20px 0; border-radius: 4px;">
  <strong>⚠️ Common Mistake:</strong> Using GET requests that modify data! Always use the appropriate HTTP method—POST, PUT, or DELETE—for state-changing operations.
</div>
        `,
        quiz: [
          {
            question: "Which HTTP method is idempotent but NOT safe?",
            options: ["GET", "POST", "PUT", "PATCH"],
            correctAnswer: 2,
            explanation:
              "PUT is idempotent (multiple identical requests have the same effect) but not safe because it modifies server state.",
          },
        ],
      },
      {
        id: "postman-ui",
        title: "Mastering the Postman Interface",
        estimatedMinutes: 12,
        content: `
# Mastering the Postman Interface: A Visual Guide

The Postman interface is designed for efficiency. Let's explore every component in detail.

## Main Interface Components

### 1. **Header Bar** (Top)
- **New Button:** Create requests, collections, environments, mock servers, monitors, or documentation
- **Import Button:** Import Postman collections, OpenAPI specs, RAML, or cURL commands
- **Runner Button:** Launch the Collection Runner for batch execution
- **Workspace Dropdown:** Switch between Personal, Team, or Public workspaces
- **Sync Status:** Shows when changes are saved to cloud
- **Settings:** Configure theme, shortcuts, certificate management, proxy settings
- **Notifications:** Alerts for monitor failures, team activity

### 2. **Sidebar** (Left) - Expandable/Collapsible
Contains three main tabs:

#### 📚 **Collections Tab**
- Hierarchical tree view of all collections
- Create folders and subfolders for organization
- Right-click context menu for renaming, duplicating, exporting
- Search within collections
- Drag-and-drop reordering

#### 🕐 **History Tab**
- Auto-saves all requests (configurable limit)
- Color-coded by HTTP method (GET=green, POST=blue, etc.)
- Search and filter history
- Save individual requests from history to collections

#### 🌍 **Environments Tab**
- Quick variable viewer and editor
- Active environment indicator
- Environment variable values in real-time

### 3. **Request Builder** (Top Right)
The heart of Postman's functionality:

#### Method Dropdown
- Click to select HTTP method
- Recent methods appear at top
- Full list includes: GET, POST, PUT, PATCH, DELETE, HEAD, OPTIONS, TRACE, CONNECT

#### URL Field
- Enter endpoint URL
- Auto-suggests from history
- Supports variables: \`{{base_url}}/users\`
- Parameter helper below the field

#### Send Button
- **Send:** Execute the request
- **Send & Download:** Save response as file
- Dropdown for retry options

#### Save Button
- Save request to collection
- Specify name and optional description
- Keyboard shortcut: Ctrl+S (Cmd+S on Mac)

#### Request Tabs (below URL field):
- **Params:** Query parameters and path variables
- **Authorization:** Auth configuration (API Key, Bearer Token, Basic Auth, OAuth 1.0/2.0, etc.)
- **Headers:** Custom HTTP headers with bulk edit option
- **Body:** Request body for POST/PUT/PATCH (form-data, x-www-form-urlencoded, raw, binary, GraphQL)
- **Pre-request Script:** JavaScript to run BEFORE request
- **Tests:** JavaScript to run AFTER request
- **Settings:** Request-specific overrides

### 4. **Response Viewer** (Bottom Right)

#### Response Tabs:
- **Body:** The actual response data
  - Pretty (formatted JSON/XML/HTML)
  - Raw (unformatted text)
  - Preview (rendered HTML)
  - Visualize (custom visualizations)

- **Cookies:** Response cookies (requires cookie interceptor)
- **Headers:** Response headers with search/filter
- **Test Results:** Pass/fail status of all tests
- **Console:** Detailed request/response logs (View → Show Postman Console)

#### Response Metadata Bar:
- **Status Code:** 200 OK, 404 Not Found, etc. (color-coded)
- **Time:** Response time in milliseconds
- **Size:** Payload size

### 5. **Status Bar** (Bottom)
- Network connectivity status
- CPU/Memory usage indicator
- Keyboard shortcuts reference

## Keyboard Shortcuts Cheat Sheet

| Action | Windows/Linux | Mac |
| :--- | :--- | :--- |
| Send Request | Ctrl+Enter | Cmd+Enter |
| Save Request | Ctrl+S | Cmd+S |
| New Request | Ctrl+N | Cmd+N |
| Open Console | Ctrl+Alt+C | Cmd+Option+C |
| Duplicate Tab | Ctrl+D | Cmd+D |
| Close Tab | Ctrl+W | Cmd+W |
| Search | Ctrl+F | Cmd+F |
| Format JSON | Ctrl+B | Cmd+B |

## Customizing Your Workspace

### Themes
Go to Settings → Themes to choose:
- **Light:** Default bright theme
- **Dark:** Popular among developers
- **Auto:** Follow system preference

### Layout Options
- **Single Column:** Traditional layout
- **Double Column:** Sidebar + two-column layout (advanced users)

### Font Sizes
Adjust editor font size for better readability (Ctrl/Cmd + or -)

## Getting Help in Postman

- **Documentation:** \`https://learning.postman.com\`
- **Support Center:** File tickets for bug reports
- **Community Forum:** Ask questions, share solutions
- **Postman Community on Slack:** Real-time chat
- **In-app Help:** Help → Search Documentation

<div style="padding: 15px; background-color: #F0FDF4; border-left: 4px solid #22C55E; margin: 20px 0; border-radius: 4px;">
  <strong>💡 Pro Tip:</strong> Press \`Ctrl/Cmd + K\` to open the command palette—quickly access any Postman feature without mouse navigation!
</div>

## Practice Exercise
Spend 5 minutes clicking through each part of the interface. Try:
1. Creating a new request (GET to \`https://jsonplaceholder.typicode.com/posts\`)
2. Saving it to a new collection called "Learning Postman"
3. Exploring the History tab to see your request
4. Opening the Console to see detailed request/response data

This hands-on familiarization will make the rest of this course much easier! 🎯
        `,
        exercise: {
          title: "Interface Exploration",
          description:
            "Navigate through the Postman interface and identify all major components.",
          starterCode:
            "Open Postman and take a screenshot of your workspace with annotations showing: Header Bar, Sidebar, Request Builder, Response Viewer, Status Bar",
        },
      },
    ],
  },
  {
    id: "requests",
    title: "2. Making HTTP Requests",
    summary:
      "Master GET, POST, PUT, PATCH, and DELETE requests with real-world examples.",
    lessons: [
      {
        id: "get-request",
        title: "Mastering GET Requests: Fetching Data",
        estimatedMinutes: 15,
        content: `
# Mastering GET Requests: Everything You Need to Know

GET requests are the most common HTTP method. Let's explore them in depth with practical examples using free public APIs.

## Anatomy of a GET Request

\`\`\`
GET /api/users?role=admin&page=2&limit=20 HTTP/1.1
Host: api.example.com
Accept: application/json
Authorization: Bearer your_token_here
User-Agent: PostmanRuntime/7.26.8
\`\`\`

### Components Explained:
1. **Method:** GET
2. **Path:** /api/users
3. **Query String:** ?role=admin&page=2&limit=20
4. **Headers:** Metadata about the request
5. **No Body:** GET requests don't have a request body

## Query Parameters Deep Dive

Query parameters allow you to filter, sort, and paginate data.

### Common Query Parameters:

| Parameter | Purpose | Example |
| :--- | :--- | :--- |
| \`limit\` | Max items per page | \`?limit=10\` |
| \`offset\` or \`page\` | Pagination | \`?offset=20\` |
| \`sort\` | Sort field | \`?sort=-createdAt\` |
| \`filter\` | Field filtering | \`?status=active\` |
| \`search\` | Text search | \`?search=postman\` |
| \`fields\` | Select specific fields | \`?fields=id,name,email\` |

### URL Encoding Special Characters
Special characters must be encoded:
- Space → \`%20\` or \`+\`
- & → \`%26\`
- = → \`%3D\`
- ? → \`%3F\`
- # → \`%23\`

**Example:** \`?name=John Doe\` → \`?name=John%20Doe\`

## Practical Examples with Public APIs

### Example 1: JSONPlaceholder (Free Fake API)

**Request:**
\`\`\`http
GET https://jsonplaceholder.typicode.com/posts HTTP/1.1
\`\`\`

**Response:**
\`\`\`json
[
  {
    "userId": 1,
    "id": 1,
    "title": "sunt aut facere repellat provident occaecati excepturi optio reprehenderit",
    "body": "quia et suscipit..."
  },
  {
    "userId": 1,
    "id": 2,
    "title": "qui est esse",
    "body": "est rerum tempore vitae..."
  }
]
\`\`\`

### Example 2: Filtering Posts by User

**Request:**
\`\`\`http
GET https://jsonplaceholder.typicode.com/posts?userId=1 HTTP/1.1
\`\`\`

### Example 3: Get Single Resource

**Request:**
\`\`\`http
GET https://jsonplaceholder.typicode.com/posts/1 HTTP/1.1
\`\`\`

**Response:**
\`\`\`json
{
  "userId": 1,
  "id": 1,
  "title": "sunt aut facere...",
  "body": "quia et suscipit..."
}
\`\`\`

### Example 4: Pagination with Placeholder

**Request:**
\`\`\`http
GET https://jsonplaceholder.typicode.com/posts?_page=2&_limit=5 HTTP/1.1
\`\`\`

### Example 5: GitHub API - Get User Repos

**Request:**
\`\`\`http
GET https://api.github.com/users/octocat/repos?sort=updated&direction=desc&per_page=5 HTTP/1.1
Accept: application/vnd.github.v3+json
User-Agent: Postman
\`\`\`

**Response Includes:**
- Repository names, descriptions, stars, forks
- Last update timestamps
- Language information

## Important GET Request Headers

| Header | Purpose | Example |
| :--- | :--- | :--- |
| \`Accept\` | Expected response format | \`Accept: application/json\` |
| \`Authorization\` | Authentication token | \`Bearer token123\` |
| \`Cache-Control\` | Cache behavior | \`Cache-Control: no-cache\` |
| \`If-Modified-Since\` | Conditional GET | \`If-Modified-Since: Wed, 21 Oct 2024 07:28:00 GMT\` |

## Common Status Codes for GET

| Status | Meaning | When It Happens |
| :--- | :--- | :--- |
| **200 OK** | Success | Resource found and returned |
| **301 Moved Permanently** | Redirect | Resource URL changed |
| **304 Not Modified** | Cache hit | Resource unchanged since last request |
| **400 Bad Request** | Client error | Invalid query parameters |
| **401 Unauthorized** | Auth required | Missing or invalid token |
| **403 Forbidden** | No permission | Authenticated but not authorized |
| **404 Not Found** | Missing | Resource doesn't exist |

## Best Practices for GET Requests

### ✅ DO:
- Use query parameters for filtering, pagination, and sorting
- Implement caching for frequently accessed data
- Use ETags for conditional requests
- Keep URLs readable and logical
- Return 404 for missing resources
- Use 304 for unchanged resources

### ❌ DON'T:
- **Never modify server state** (no data creation, updates, or deletion)
- Don't send sensitive data in URLs (use Authorization header)
- Don't create overly complex URLs (max 2000 characters)
- Don't expose internal implementation details in URLs

## Hands-On Exercise

Try these GET requests in Postman:

### Beginner Exercise:
1. GET \`https://jsonplaceholder.typicode.com/users\`
2. GET \`https://jsonplaceholder.typicode.com/users/3\`
3. GET \`https://jsonplaceholder.typicode.com/posts?userId=3\`

### Intermediate Exercise:
1. GET \`https://api.github.com/users/github/repos\`
2. Filter only repositories with \`?language=javascript\`
3. Sort by stars: \`?sort=stars&order=desc\`

### Advanced Exercise:
Using the Pokémon API (\`https://pokeapi.co/api/v2/\`):
1. GET all Pokémon (limit to 20)
2. GET a specific Pokémon by ID or name
3. GET the evolution chain for that Pokémon

<div style="padding: 15px; background-color: #F0FDF4; border-left: 4px solid #22C55E; margin: 20px 0; border-radius: 4px;">
  <strong>💡 Pro Tip:</strong> Use Postman's "Params" tab instead of manually typing query parameters! It automatically handles URL encoding and makes parameters easier to manage.
</div>

## Debugging GET Requests

When a GET request fails, check:

1. **Is the URL correct?** Verify endpoint and path
2. **Are parameters properly encoded?** Special characters need encoding
3. **Are required headers present?** Accept, Authorization, etc.
4. **Is the resource ID valid?** Does it exist?
5. **Check the Console** (View → Show Postman Console) for detailed request/response

## Real-World API Example: Weather API

\`\`\`http
GET https://api.openweathermap.org/data/2.5/weather?q=London&appid=YOUR_API_KEY&units=metric HTTP/1.1
\`\`\`

This returns:
\`\`\`json
{
  "main": {
    "temp": 15.3,
    "humidity": 72,
    "pressure": 1012
  },
  "weather": [{
    "description": "light rain",
    "icon": "10d"
  }],
  "wind": {
    "speed": 4.1
  }
}
\`\`\`

Now you're ready to start making GET requests in Postman! 🚀
        `,
        quiz: [
          {
            question:
              "Which of the following should NEVER be done in a GET request?",
            options: [
              "Use query parameters for pagination",
              "Include an Authorization header",
              "Update a user's email address in the database",
              "Request JSON response format",
            ],
            correctAnswer: 2,
            explanation:
              "GET requests must be safe and idempotent—they should never modify server state, including updating data in databases.",
          },
        ],
        exercise: {
          title: "Building a News Aggregator",
          description:
            "Use the Hacker News API to fetch top stories and their details.",
          starterCode: `// Step 1: GET https://hacker-news.firebaseio.com/v0/topstories.json
// Step 2: Get first 5 story IDs
// Step 3: For each ID, GET https://hacker-news.firebaseio.com/v0/item/{id}.json
// Step 4: Display story titles and URLs
`,
          solution:
            "Use collection runner or write JavaScript in Pre-request Script to iterate through IDs.",
        },
      },
      {
        id: "post-request",
        title: "Creating Resources with POST Requests",
        estimatedMinutes: 15,
        content: `
# Creating Resources with POST Requests

POST requests are used to create new resources on the server. Unlike GET, POST requests include a request body containing the data to be created.

## Anatomy of a POST Request

\`\`\`http
POST /api/users HTTP/1.1
Host: api.example.com
Content-Type: application/json
Content-Length: 78
Authorization: Bearer your_token

{
  "name": "Jane Smith",
  "email": "jane@example.com",
  "role": "developer"
}
\`\`\`

## Request Body Formats

Postman supports multiple body formats for POST requests:

### 1. **Raw JSON** (Most Common for REST APIs)
\`\`\`json
{
  "username": "john_doe",
  "email": "john@example.com",
  "password": "securePass123",
  "preferences": {
    "theme": "dark",
    "notifications": true
  }
}
\`\`\`

### 2. **x-www-form-urlencoded** (Traditional Form Data)
\`\`\`
username=john_doe&email=john%40example.com&password=securePass123
\`\`\`

### 3. **Form-Data** (File Uploads)
\`\`\`
------WebKitFormBoundary
Content-Disposition: form-data; name="avatar"; filename="photo.jpg"
Content-Type: image/jpeg

[binary data]
------WebKitFormBoundary--
\`\`\`

### 4. **GraphQL** (Query Language)
\`\`\`graphql
{
  "query": "mutation { createUser(name: "John Doe", email: "john@example.com") { id name } }"
}
\`\`\`

## Practical Examples

### Example 1: Create a New Post (JSONPlaceholder)

**Request:**
\`\`\`http
POST https://jsonplaceholder.typicode.com/posts HTTP/1.1
Content-Type: application/json

{
  "title": "My Awesome Blog Post",
  "body": "This is the content of my blog post. It's really interesting!",
  "userId": 1
}
\`\`\`

**Response (201 Created):**
\`\`\`json
{
  "title": "My Awesome Blog Post",
  "body": "This is the content of my blog post. It's really interesting!",
  "userId": 1,
  "id": 101
}
\`\`\`

### Example 2: User Registration

**Request:**
\`\`\`http
POST https://reqres.in/api/register HTTP/1.1
Content-Type: application/json

{
  "email": "eve.holt@reqres.in",
  "password": "pistol"
}
\`\`\`

**Response:**
\`\`\`json
{
  "id": 4,
  "token": "QpwL5tke4Pnpja7X4"
}
\`\`\`

### Example 3: File Upload to httpbin

**Request:**
\`\`\`http
POST https://httpbin.org/post HTTP/1.1
Content-Type: multipart/form-data; boundary=---WebKitFormBoundary

---WebKitFormBoundary
Content-Disposition: form-data; name="file"; filename="document.pdf"
Content-Type: application/pdf

[PDF binary data]
---WebKitFormBoundary
Content-Disposition: form-data; name="description"

My uploaded PDF file
---WebKitFormBoundary--
\`\`\`

### Example 4: Create a GitHub Gist

**Request:**
\`\`\`http
POST https://api.github.com/gists HTTP/1.1
Authorization: Bearer YOUR_GITHUB_TOKEN
Content-Type: application/json

{
  "description": "My first Gist from Postman",
  "public": true,
  "files": {
    "hello.js": {
      "content": "console.log('Hello from Postman!');"
    }
  }
}
\`\`\`

## Expected Responses

| Status Code | Meaning | Body Content |
| :--- | :--- | :--- |
| **201 Created** | Resource created successfully | New resource data (often with ID) |
| **202 Accepted** | Request accepted for processing | Task ID or status URL |
| **400 Bad Request** | Invalid data format | Validation error details |
| **401 Unauthorized** | Missing/invalid auth | Authentication required message |
| **403 Forbidden** | Not authorized to create | Permission denied message |
| **409 Conflict** | Resource already exists | Conflict details |
| **422 Unprocessable Entity** | Validation failed | Specific validation errors |

## Best Practices for POST Requests

### ✅ DO:
- Include a \`Content-Type\` header (usually \`application/json\`)
- Validate data on client side before sending
- Return a 201 status with Location header pointing to new resource
- Include the created resource's ID in response
- Use HTTPS for all POST requests (sensitive data in body)

### ❌ DON'T:
- Don't make POST requests idempotent (each POST should create a new resource)
- Don't use GET parameters for creating data
- Don't send sensitive data in URLs (even with POST, use body)
- Don't forget to handle validation errors gracefully

## Common Headers for POST

| Header | Value | Purpose |
| :--- | :--- | :--- |
| \`Content-Type\` | \`application/json\` | Indicates JSON body |
| \`Accept\` | \`application/json\` | Expect JSON response |
| \`Authorization\` | \`Bearer token\` | Authentication |
| \`Content-Length\` | \`123\` | Body size (auto-added) |
| \`X-Request-ID\` | \`uuid\` | Request tracking |

## Error Handling Examples

### Validation Error (400)
\`\`\`json
{
  "error": "Validation failed",
  "details": {
    "email": "Email format is invalid",
    "password": "Password must be at least 8 characters"
  }
}
\`\`\`

### Authentication Error (401)
\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid API key"
}
\`\`\`

## Hands-On Exercises

### Beginner Exercise:
Create a todo item using JSONPlaceholder:
\`\`\`json
POST https://jsonplaceholder.typicode.com/todos
{
  "title": "Learn Postman",
  "completed": false,
  "userId": 1
}
\`\`\`

### Intermediate Exercise:
Register a new user on ReqRes:
\`\`\`json
POST https://reqres.in/api/register
{
  "email": "your.email@example.com",
  "password": "yourPassword123"
}
\`\`\`

### Advanced Exercise:
Create a repository using GitHub API:
1. First, generate a Personal Access Token on GitHub
2. POST \`https://api.github.com/user/repos\`
3. Body: \`{"name": "postman-test-repo", "description": "Created with Postman", "private": false}\`
4. Include Authorization header with your token

## Testing Your POST Requests

Add these tests to verify your POST request worked:

\`\`\`javascript
// Test 1: Check status code
pm.test("Status code is 201 Created", function () {
    pm.response.to.have.status(201);
});

// Test 2: Response has expected fields
pm.test("Response contains created resource ID", function () {
    var jsonData = pm.response.json();
    pm.expect(jsonData).to.have.property('id');
});

// Test 3: Location header exists
pm.test("Location header is present", function () {
    pm.expect(pm.response.headers.get('Location')).to.exist;
});

// Test 4: Response time is reasonable
pm.test("Response time is less than 1000ms", function () {
    pm.expect(pm.response.responseTime).to.be.below(1000);
});
\`\`\`

<div style="padding: 15px; background-color: #F0FDF4; border-left: 4px solid #22C55E; margin: 20px 0; border-radius: 4px;">
  <strong>💡 Pro Tip:</strong> Always look for the Location header in POST responses—it tells you the URL of your newly created resource! Example: \`Location: /users/123\`
</div>

## Security Considerations

1. **Use HTTPS only** - POST data travels in request body but still needs encryption
2. **Don't log request bodies** - They contain sensitive data
3. **Implement rate limiting** - Prevent abuse of creation endpoints
4. **Validate all input** - Never trust client-side validation alone
5. **Use CSRF tokens** - Protect against cross-site request forgery

Now go create some resources with POST requests! 🎯
        `,
        quiz: [
          {
            question:
              "What is the correct HTTP status code for a successful POST request that creates a new resource?",
            options: [
              "200 OK",
              "201 Created",
              "202 Accepted",
              "204 No Content",
            ],
            correctAnswer: 1,
            explanation:
              "201 Created is the standard response when a POST request successfully creates a new resource. 200 OK is generic success, 202 means accepted for processing, and 204 means success with no response body.",
          },
        ],
      },
      {
        id: "put-patch-delete",
        title: "Updating and Deleting: PUT, PATCH, and DELETE",
        estimatedMinutes: 15,
        content: `
# Updating and Deleting: PUT, PATCH, and DELETE

After creating resources with POST, you need to update and delete them. PUT, PATCH, and DELETE handle these operations with important differences you must understand.

## PUT vs PATCH: The Critical Difference

| Aspect | PUT | PATCH |
| :--- | :--- | :--- |
| **Action** | Complete replacement | Partial update |
| **Idempotent** | ✅ Yes | ❌ No |
| **Required Fields** | All fields | Only fields to change |
| **Missing Fields** | Set to null/default | Left unchanged |
| **Use Case** | Full document update | Small modifications |

### PUT Example: Complete Replacement

**Initial Resource:**
\`\`\`json
{
  "id": 1,
  "name": "John Doe",
  "email": "john@old.com",
  "age": 25,
  "city": "New York"
}
\`\`\`

**PUT Request (updating name and email only):**
\`\`\`http
PUT /api/users/1 HTTP/1.1
Content-Type: application/json

{
  "name": "John Smith",
  "email": "john@new.com"
}
\`\`\`

**Result (age and city are GONE):**
\`\`\`json
{
  "id": 1,
  "name": "John Smith",
  "email": "john@new.com"
}
\`\`\`

### PATCH Example: Partial Update

**Same Initial Resource:**
\`\`\`json
{
  "id": 1,
  "name": "John Doe",
  "email": "john@old.com",
  "age": 25,
  "city": "New York"
}
\`\`\`

**PATCH Request (updating name and email):**
\`\`\`http
PATCH /api/users/1 HTTP/1.1
Content-Type: application/json

{
  "name": "John Smith",
  "email": "john@new.com"
}
\`\`\`

**Result (age and city PRESERVED):**
\`\`\`json
{
  "id": 1,
  "name": "John Smith",
  "email": "john@new.com",
  "age": 25,
  "city": "New York"
}
\`\`\`

## Practical Examples

### Example 1: PUT - Complete Update (JSONPlaceholder)

**Request:**
\`\`\`http
PUT https://jsonplaceholder.typicode.com/posts/1 HTTP/1.1
Content-Type: application/json

{
  "id": 1,
  "title": "Completely New Title",
  "body": "Brand new content that replaces everything",
  "userId": 1
}
\`\`\`

**Response:**
\`\`\`json
{
  "id": 1,
  "title": "Completely New Title",
  "body": "Brand new content that replaces everything",
  "userId": 1
}
\`\`\`

### Example 2: PATCH - Partial Update

**Request:**
\`\`\`http
PATCH https://jsonplaceholder.typicode.com/posts/1 HTTP/1.1
Content-Type: application/json

{
  "title": "Updated Title Only"
}
\`\`\`

**Response (body and userId unchanged):**
\`\`\`json
{
  "id": 1,
  "title": "Updated Title Only",
  "body": "original body content remains",
  "userId": 1
}
\`\`\`

### Example 3: PATCH with JSON Patch Format (RFC 6902)

Some APIs support the official JSON Patch format:

**Request:**
\`\`\`http
PATCH /api/users/1 HTTP/1.1
Content-Type: application/json-patch+json

[
  { "op": "replace", "path": "/email", "value": "new@email.com" },
  { "op": "add", "path": "/phone", "value": "555-1234" },
  { "op": "remove", "path": "/temporary_field" }
]
\`\`\`

### Example 4: DELETE Request

**Request:**
\`\`\`http
DELETE https://jsonplaceholder.typicode.com/posts/1 HTTP/1.1
\`\`\`

**Response:** 200 OK (or 204 No Content) with possibly an empty body

### Example 5: Conditional Delete (with If-Match)

**Request:**
\`\`\`http
DELETE https://api.example.com/products/123 HTTP/1.1
If-Match: "etag_abc123"
\`\`\`

**Response 412 Precondition Failed** if the ETag doesn't match (resource was modified since you fetched it)

## DELETE Request Deep Dive

### Successful DELETE Responses

| Status | When to Use |
| :--- | :--- |
| **200 OK** | Resource deleted, return deleted data |
| **202 Accepted** | Deletion queued for processing |
| **204 No Content** | Deleted, no response body |
| **404 Not Found** | Resource already deleted or never existed |

### DELETE Best Practices

**✅ DO:**
- Make DELETE idempotent (second delete returns 404)
- Return 404 for already-deleted resources
- Ask for confirmation on destructive deletes
- Log deletion for audit purposes

**❌ DON'T:**
- Don't return a response body with 204
- Don't require a request body
- Don't make DELETE non-idempotent

## Complete CRUD Example

Using ReqRes API (\`https://reqres.in\`):

### CREATE (POST)
\`\`\`http
POST https://reqres.in/api/users
{
  "name": "John Doe",
  "job": "Developer"
}
// Returns: { "name": "John Doe", "job": "Developer", "id": "123", "createdAt": "2024-01-01T00:00:00Z" }
\`\`\`

### READ (GET)
\`\`\`http
GET https://reqres.in/api/users/2
// Returns user details
\`\`\`

### UPDATE (PUT)
\`\`\`http
PUT https://reqres.in/api/users/2
{
  "name": "John Smith",
  "job": "Senior Developer"
}
// Returns: { "name": "John Smith", "job": "Senior Developer", "updatedAt": "2024-01-01T00:00:00Z" }
\`\`\`

### UPDATE (PATCH)
\`\`\`http
PATCH https://reqres.in/api/users/2
{
  "job": "Lead Developer"
}
// Only job field updated
\`\`\`

### DELETE
\`\`\`http
DELETE https://reqres.in/api/users/2
// Returns: 204 No Content
\`\`\`

## Testing PUT/PATCH/DELETE Requests

### Tests for PUT/PATCH:
\`\`\`javascript
// Test successful update
pm.test("Status code is 200 or 204", function () {
    pm.expect(pm.response.code).to.be.oneOf([200, 201, 204]);
});

// Test that fields were updated correctly
pm.test("Name was updated", function () {
    var jsonData = pm.response.json();
    pm.expect(jsonData.name).to.equal("John Smith");
});

// Test response time
pm.test("Response time < 1000ms", function () {
    pm.expect(pm.response.responseTime).to.be.below(1000);
});
\`\`\`

### Tests for DELETE:
\`\`\`javascript
// Test successful deletion
pm.test("Delete successful", function () {
    pm.expect(pm.response.code).to.be.oneOf([200, 202, 204]);
});

// Test that resource no longer exists
pm.test("Resource cannot be retrieved after delete", function () {
    // You would need a subsequent GET request here
    pm.expect(true).to.be.true; // Placeholder
});
\`\`\`

## Common Status Codes for Updates/Deletes

| Method | Common Status Codes |
| :--- | :--- |
| **PUT** | 200 (OK), 201 (Created), 204 (No Content), 400 (Bad Request), 404 (Not Found), 409 (Conflict), 412 (Precondition Failed) |
| **PATCH** | 200 (OK), 204 (No Content), 400 (Bad Request), 404 (Not Found), 409 (Conflict), 415 (Unsupported Media Type), 422 (Unprocessable Entity) |
| **DELETE** | 200 (OK), 202 (Accepted), 204 (No Content), 404 (Not Found), 409 (Conflict), 410 (Gone) |

## Best Practices Summary

### PUT Best Practices:
1. Require complete resource representation
2. Replace all fields, including optional ones
3. Create resource if it doesn't exist (optional)
4. Return 200 or 204 on success

### PATCH Best Practices:
1. Only include fields to modify
2. Use JSON Patch format for complex operations
3. Validate patches before applying
4. Return full resource after update (optional)

### DELETE Best Practices:
1. Make idempotent (repeatable)
2. Consider soft deletion (mark as deleted, keep data)
3. Return appropriate status codes
4. Don't use request body

## Hands-On Exercises

### Beginner Exercise:
1. PUT update a post on JSONPlaceholder
2. PATCH the same post with different data
3. DELETE the post
4. GET the deleted post to verify 404

### Intermediate Exercise:
Create a simple REST API flow:
1. POST a new book (title, author, year)
2. PUT to replace with new edition (all new data)
3. PATCH to update only the year
4. DELETE the book
5. Try to GET after delete

### Advanced Exercise:
Using GitHub API with ETags:
1. GET a file from a repository (capture ETag header)
2. PUT to update the file (include ETag in If-Match)
3. Try PUT again with wrong ETag (should fail with 412)
4. DELETE the file

<div style="padding: 15px; background-color: #FEFCE8; border-left: 4px solid #EAB308; margin: 20px 0; border-radius: 4px;">
  <strong>⚠️ Common Mistake:</strong> Using PUT when you want PATCH. Remember: PUT replaces everything (destructive), PATCH is additive (non-destructive). If you only want to change a phone number, use PATCH!
</div>

Now you understand the full CRUD (Create, Read, Update, Delete) cycle! 🎯
        `,
        quiz: [
          {
            question:
              "What happens to fields not included in a PUT request vs a PATCH request?",
            options: [
              "PUT preserves them, PATCH removes them",
              "PUT removes them (sets to null), PATCH preserves them",
              "Both preserve all fields",
              "Both remove unspecified fields",
            ],
            correctAnswer: 1,
            explanation:
              "PUT replaces the entire resource, so unspecified fields are set to default/null. PATCH only changes specified fields, leaving others unchanged.",
          },
        ],
      },
      {
        id: "authorization",
        title: "API Authentication and Authorization",
        estimatedMinutes: 20,
        content: `
# API Authentication and Authorization: A Complete Guide

Most production APIs require authentication to control access. Postman makes implementing various authentication methods straightforward.

## Authentication vs Authorization

| Term | Meaning | Example |
| :--- | :--- | :--- |
| **Authentication** | Who you are | Login with username/password |
| **Authorization** | What you can do | User can read but not delete |

## Authentication Methods in Postman

### 1. **No Auth**
For public APIs or development endpoints.
**Example:** JSONPlaceholder, public weather APIs

### 2. **API Key**
Most common for simple APIs. A unique key sent with each request.

**Ways to send API Key:**
- Header: \`X-API-Key: your_key_here\`
- Query Parameter: \`?api_key=your_key_here\`
- Body field (less common)

**Postman Setup:**
1. Authorization tab → Type: API Key
2. Key: \`X-API-Key\` (or whatever header name)
3. Value: \`{{api_key}}\` (use variable!)
4. Add to: Header

**Real Example - OpenWeatherMap:**
\`\`\`http
GET https://api.openweathermap.org/data/2.5/weather?q=London&appid=YOUR_API_KEY
\`\`\`

### 3. **Bearer Token (JWT)**
Used for token-based authentication, especially with JWTs (JSON Web Tokens).

**Structure:** \`Authorization: Bearer <token>\`

**Postman Setup:**
1. Authorization tab → Type: Bearer Token
2. Token: \`{{jwt_token}}\`

**Real Example - Auth0 Protected API:**
\`\`\`http
GET https://api.example.com/users/me
Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...
\`\`\`

### 4. **Basic Auth**
Username and password encoded in Base64.

**How it works:**
\`\`\`
Username: admin
Password: secret
Encoded: Basic YWRtaW46c2VjcmV0
\`\`\`

**Postman Setup:**
1. Authorization tab → Type: Basic Auth
2. Username: \`admin\`
3. Password: \`{{password}}\`
4. Postman automatically encodes and adds header

**Real Example - Confluence API:**
\`\`\`http
GET https://your-domain.atlassian.net/wiki/rest/api/space
Authorization: Basic dXNlcm5hbWU6cGFzc3dvcmQ=
\`\`\`

### 5. **OAuth 2.0**
Industry-standard protocol for delegated access. Complex but very secure.

**OAuth 2.0 Flow:**
1. User authorizes app
2. App receives authorization code
3. App exchanges code for access token
4. App uses token to access API

**Postman Setup:**
1. Authorization tab → Type: OAuth 2.0
2. Click "Get New Access Token"
3. Configure:
   - Token Name: My Token
   - Grant Type: Authorization Code / Client Credentials / Password
   - Callback URL: \`https://oauth.pstmn.io/v1/callback\`
   - Auth URL, Access Token URL
   - Client ID, Client Secret
   - Scope, State
4. Click "Request Token"
5. Use token

**Postman OAuth Features:**
- Auto-refresh tokens before expiry
- Built-in callback URL handler
- Support for all OAuth 2.0 grant types

### 6. **Digest Auth**
More secure than Basic Auth. Client sends hashed credentials.

**Postman Setup:**
1. Authorization tab → Type: Digest Auth
2. Username, Password
3. Optional: Realm, Nonce, Algorithm

### 7. **AWS Signature**
For Amazon Web Services APIs.

**Postman Setup:**
1. Authorization tab → Type: AWS Signature
2. AccessKey, SecretKey
3. AWS Region, Service Name (e.g., s3, dynamodb)

### 8. **NTLM Authentication** (Windows)
Used by older Windows-based servers (SharePoint, Exchange).

**Postman Setup:**
1. Authorization tab → Type: NTLM Authentication
2. Username, Password
3. Domain, Workstation (optional)

### 9. **Hawk Authentication**
HTTP authentication scheme with replay protection.

**Postman Setup:**
1. Authorization tab → Type: Hawk Authentication
2. Hawk ID, Hawk Key
3. Algorithm (SHA256, SHA1)

## Practical Authentication Examples

### Example 1: API Key - News API

**Setup:**
1. Sign up at \`https://newsapi.org\`
2. Get your API key
3. In Postman: Authorization → API Key
   - Key: \`X-Api-Key\`
   - Value: \`your_actual_api_key\`
   - Add to: Header

**Request:**
\`\`\`http
GET https://newsapi.org/v2/top-headlines?country=us
\`\`\`

### Example 2: Bearer Token - GitHub API

**Setup:**
1. GitHub → Settings → Developer settings → Personal access tokens
2. Generate token with appropriate scopes
3. In Postman: Authorization → Bearer Token
4. Token: \`your_github_token\`

**Request:**
\`\`\`http
GET https://api.github.com/user
\`\`\`

### Example 3: OAuth 2.0 - Spotify API

**Setup in Postman:**
1. Create Spotify app at \`https://developer.spotify.com\`
2. Get Client ID and Secret
3. In Postman OAuth 2.0:
   - Grant Type: Client Credentials
   - Access Token URL: \`https://accounts.spotify.com/api/token\`
   - Client ID and Secret
   - Scope: \`user-read-private user-read-email\`
4. Get token and use it

**Request:**
\`\`\`http
GET https://api.spotify.com/v1/me
\`\`\`

## Environment Variables for Authentication

**Never hardcode credentials!** Use environment variables:

### Create environment variables:
\`\`\`json
{
  "api_key": "abc123xyz789",
  "bearer_token": "eyJhbGciOiJIUzI1NiIs...",
  "username": "api_user",
  "password": "secure_password",
  "client_id": "your_client_id",
  "client_secret": "your_client_secret"
}
\`\`\`

### Use in Authorization:
- API Key value: \`{{api_key}}\`
- Bearer Token: \`{{bearer_token}}\`
- Basic Auth: Use variables for username/password

## Testing Authentication

### Test if authentication works:
\`\`\`javascript
// Check for successful auth (no 401/403)
pm.test("Authentication successful", function () {
    pm.expect(pm.response.code).to.not.be.oneOf([401, 403]);
});

// For OAuth, check token exists
pm.test("Access token received", function () {
    var jsonData = pm.response.json();
    pm.expect(jsonData).to.have.property('access_token');
});

// Set token as variable for future requests
pm.test("Save token for later", function () {
    var jsonData = pm.response.json();
    pm.environment.set("auth_token", jsonData.access_token);
});
\`\`\`

## Security Best Practices

### ✅ **DO:**
- Use environment variables for credentials
- Never commit tokens to version control
- Use short-lived tokens when possible
- Implement token refresh mechanisms
- Use HTTPS for all authenticated requests
- Rotate API keys periodically
- Use OAuth 2.0 for third-party apps

### ❌ **DON'T:**
- Never hardcode credentials in requests
- Don't share tokens in screenshots
- Don't log tokens in tests
- Don't use Basic Auth over HTTP (only HTTPS)
- Don't store tokens in global variables (use environment)

## Common Authentication Errors

| Error | Status | Cause | Solution |
| :--- | :--- | :--- | :--- |
| No token provided | 401 | Missing Authorization header | Add auth to request |
| Invalid token | 401 | Token expired or malformed | Get new token |
| Insufficient scope | 403 | Token missing required permissions | Request wider scope |
| Rate limiting | 429 | Too many requests | Add delays, increase limits |
| IP whitelisting | 403 | Request from unauthorized IP | Use allowed IP or proxy |

## Hands-On Exercises

### Beginner Exercise: API Key
1. Get free API key from OpenWeatherMap
2. Configure Postman with API Key auth
3. Fetch weather for your city

### Intermediate Exercise: Bearer Token
1. Generate GitHub personal access token
2. Configure Bearer Token auth
3. GET your user profile
4. GET your repositories
5. Create a new gist

### Advanced Exercise: OAuth 2.0 Flow
1. Register app with Google OAuth 2.0
2. Configure OAuth 2.0 in Postman
3. Complete authorization flow
4. Use token to access Google Sheets API
5. Implement token auto-refresh

### Expert Exercise: Multiple Auth Methods
Build a collection that:
1. Gets token using OAuth 2.0
2. Stores token in environment
3. Uses Bearer Token for subsequent requests
4. Refreshes token when expired
5. Handles 401 errors gracefully

## Authentication in Collection Runner

When running multiple requests:

1. **Pre-request Script** for login:
\`\`\`javascript
// Get token before collection runs
const loginRequest = {
  url: pm.environment.get("auth_url"),
  method: 'POST',
  header: 'Content-Type: application/json',
  body: {
    mode: 'raw',
    raw: JSON.stringify({
      username: pm.environment.get("username"),
      password: pm.environment.get("password")
    })
  }
};

pm.sendRequest(loginRequest, (err, res) => {
  pm.environment.set("token", res.json().token);
});
\`\`\`

2. **Collection variables** for auth
3. **Folder-level auth** for grouped requests

<div style="padding: 15px; background-color: #F0FDF4; border-left: 4px solid #22C55E; margin: 20px 0; border-radius: 4px;">
  <strong>💡 Pro Tip:</strong> Use Postman's "Inherit auth from parent" feature! Set authentication once at the collection or folder level, and all child requests will use it automatically.
</div>

Now you're ready to securely authenticate with any API! 🔐
        `,
        quiz: [
          {
            question:
              "Which authentication method encodes credentials in Base64 and sends them with every request?",
            options: ["Bearer Token", "API Key", "Basic Auth", "OAuth 2.0"],
            correctAnswer: 2,
            explanation:
              "Basic Auth encodes username:password in Base64 and sends in the Authorization header. Bearer Token uses a token, API Key is custom header/param, OAuth 2.0 uses tokens with refresh capabilities.",
          },
        ],
      },
    ],
  },
  {
    id: "collections-environments",
    title: "3. Collections & Environments",
    summary:
      "Learn to organize requests, manage variables, and work efficiently across different environments.",
    lessons: [
      {
        id: "collections",
        title: "Mastering Collections: Organization & Collaboration",
        estimatedMinutes: 12,
        content: `
# Mastering Collections: The Power of Organization

Collections are the foundation of organized API testing in Postman. They help you group related requests, share work with teams, and automate testing.

## What Are Collections?

A collection is a folder-like structure that contains:
- API requests (GET, POST, PUT, DELETE, etc.)
- Subfolders for logical grouping
- Authorization settings (inheritable)
- Pre-request scripts (run before each request)
- Tests (run after each request)
- Variables (collection-scoped)
- Documentation

## Creating Your First Collection

### Method 1: From Scratch
1. Click "New" → "Collection"
2. Name: "My Learning API"
3. Description: "Collection for learning Postman"
4. Click "Create"

### Method 2: Import from cURL
\`\`\`bash
curl -X GET "https://api.example.com/users" -H "Authorization: Bearer token"
\`\`\`
1. Click "Import" → "Raw text"
2. Paste cURL command
3. Postman creates request and optional collection

### Method 3: Save from History
1. Open History tab
2. Hover over any request
3. Click save icon
4. Choose existing or new collection

## Collection Structure Best Practices

### Recommended Organization:

\`\`\`
📁 E-Commerce API
  ├── 📁 Authentication
  │   ├── POST Register
  │   ├── POST Login
  │   └── POST Refresh Token
  ├── 📁 Products
  │   ├── GET All Products
  │   ├── GET Single Product
  │   ├── POST Create Product
  │   ├── PUT Update Product
  │   └── DELETE Product
  ├── 📁 Orders
  │   ├── GET My Orders
  │   ├── POST Create Order
  │   ├── GET Order Details
  │   └── PATCH Update Order Status
  └── 📁 Users
      ├── GET Profile
      ├── PATCH Update Profile
      └── DELETE Account
\`\`\`

## Collection Features Deep Dive

### 1. **Collection Variables**
Variables scoped to the entire collection:

**Setup:**
1. Click collection → Variables tab
2. Add variables:
   - \`base_url\`: \`https://api.example.com/v1\`
   - \`api_version\`: \`v1\`
   - \`timeout\`: \`5000\`

**Usage in requests:**
\`\`\`
{{base_url}}/users
{{base_url}}/{{api_version}}/products
\`\`\`

### 2. **Collection Authorization**
Set once, use everywhere:

1. Collection → Authorization tab
2. Choose method (e.g., Bearer Token)
3. Value: \`{{auth_token}}\`
4. Child requests inherit automatically

### 3. **Collection Pre-request Scripts**
Run before EVERY request in collection:

\`\`\`javascript
// Generate timestamp for all requests
const timestamp = new Date().toISOString();
pm.collectionVariables.set("request_timestamp", timestamp);

// Log request start
console.log(\`Request started at: \${timestamp}\`);

// Add common header to all requests
pm.request.headers.add({
    key: "X-Request-ID",
    value: pm.variables.replaceIn("{{$guid}}")
});
\`\`\`

### 4. **Collection Tests**
Run after EVERY request in collection:

\`\`\`javascript
// Global assertions for all requests
pm.test("Response time is acceptable", function () {
    pm.expect(pm.response.responseTime).to.be.below(1000);
});

pm.test("Status is not server error", function () {
    pm.expect(pm.response.code).to.be.below(500);
});

pm.test("Response has valid JSON", function () {
    pm.response.to.be.json;
});
\`\`\`

### 5. **Collection Documentation**
Auto-generated from requests:

**Add descriptions:**
1. Click collection → Description
2. Add markdown documentation
3. Add examples and links

**Generate docs:**
1. Click "..." on collection
2. "View documentation"
3. Publish to web (public URL)

## Advanced Collection Features

### 1. **Collection Runner**
Execute entire collection sequentially:

**Setup:**
1. Click "Runner" (top left)
2. Select your collection
3. Configure:
   - Environment
   - Iterations (number of runs)
   - Delay between requests
   - Data file (CSV/JSON for data-driven tests)

**Run options:**
- **Keep variable values:** Persist changes
- **Save responses:** Save to file
- **Run in separate delays:** Simulate real user

### 2. **Data-Driven Testing**

**Create data file (users.csv):**
\`\`\`csv
username,email,expected_status
john, john@test.com, 201
jane, jane@test.com, 201
admin, admin@test.com, 403
\`\`\`

**Collection script:**
\`\`\`javascript
// Pre-request script
pm.variables.set("username", data.username);
pm.variables.set("email", data.email);

// Test script
pm.test(\`Create user \${data.username}\`, function () {
    pm.response.to.have.status(data.expected_status);
});
\`\`\`

### 3. **Folders within Collections**

**Folder-level settings:**
- Independent pre-request scripts
- Folder-specific tests
- Override collection auth if needed

**Example:**
\`\`\`
📁 Admin Endpoints
  ├── Auth: Admin token required
  ├── Pre-request: Check admin rights
  └── Requests: User management, analytics

📁 Public Endpoints
  ├── Auth: None
  ├── Pre-request: None
  └── Requests: Login, register, public data
\`\`\`

### 4. **Request Reordering**
- Drag and drop anywhere in collection
- Use for logical flow (login → get data → update → delete)
- Runner follows your order

## Collaboration Features

### 1. **Sharing Collections**

**Share via link:**
1. Collection → Share
2. Create public link
3. Anyone can view/import

**Share with team:**
1. Create Workspace (Team required)
2. Add team members
3. Set permissions (view/edit)

**Export collection:**
1. Click "..." → Export
2. Choose format:
   - **Collection v2.1** (recommended)
   - **Collection v2** (legacy)
3. Share JSON file

### 2. **Version Control**

**Postman's built-in versioning:**
- Automatic change tracking
- Compare versions
- Restore previous versions

**Git integration:**
1. Export collection
2. Commit to Git repository
3. Team members import from Git

### 3. **Comments and Feedback**
1. Select any request
2. Click "Add comment"
3. @mention team members
4. Resolve when addressed

## Real-World Collection Examples

### Example 1: REST API Testing Suite

\`\`\`
📁 User Management API
  ├── 📁 Setup
  │   ├── POST Get Auth Token
  │   └── POST Create Test User
  ├── 📁 CRUD Tests
  │   ├── GET Users (List)
  │   ├── POST Create User
  │   ├── GET Single User
  │   ├── PUT Update User
  │   ├── PATCH Partial Update
  │   └── DELETE User
  ├── 📁 Edge Cases
  │   ├── GET Nonexistent User (404)
  │   ├── POST Invalid Data (400)
  │   ├── PUT Without Auth (401)
  │   └── DELETE Without Permission (403)
  └── 📁 Cleanup
      ├── DELETE Test Data
      └── POST Revoke Token
\`\`\`

### Example 2: GraphQL API Collection

\`\`\`
📁 GraphQL API
  ├── 📁 Queries
  │   ├── Get User
  │   ├── List Products
  │   └── Search Orders
  ├── 📁 Mutations
  │   ├── Create Product
  │   ├── Update Order
  │   └── Delete User
  └── 📁 Subscriptions
      ├── OnOrderCreated
      └── OnProductUpdated
\`\`\`

## Collection Best Practices

### ✅ **DO:**
- Use meaningful names (avoid "Test 1", "New Request")
- Add descriptions to requests
- Use folders for logical grouping
- Set collection-level auth when possible
- Use variables for any hardcoded values
- Version control your collections
- Write tests for each request
- Document API endpoints

### ❌ **DON'T:**
- Don't create massive, flat collections (over 50 requests)
- Don't hardcode credentials
- Don't ignore failed tests
- Don't share collections with sensitive data
- Don't forget to update documentation

## Exporting and Importing

### Export formats:
\`\`\`json
{
  "info": {
    "name": "My Collection",
    "schema": "https://schema.getpostman.com/json/collection/v2.1.0/collection.json"
  },
  "item": [...]
}
\`\`\`

### Import from:
- Postman collections (JSON)
- OpenAPI/Swagger specs
- RAML
- GraphQL schemas
- cURL commands
- WADL

## Hands-On Exercises

### Beginner Exercise:
Create a collection for testing JSONPlaceholder:
1. Create "JSONPlaceholder Tests" collection
2. Add GET /posts request
3. Add GET /posts/1 request
4. Add POST /posts request
5. Organize in folders

### Intermediate Exercise:
Build an e-commerce API test suite:
1. Create folder structure (Auth, Products, Cart, Orders)
2. Set collection variable for base URL
3. Configure collection-level auth
4. Add pre-request script for request logging
5. Add common tests for response time

### Advanced Exercise:
Create data-driven test collection:
1. Create users.csv with test data
2. Write pre-request script to use CSV data
3. Configure collection runner
4. Run multiple iterations
5. Export results as JSON/HTML

<div style="padding: 15px; background-color: #F0FDF4; border-left: 4px solid #22C55E; margin: 20px 0; border-radius: 4px;">
  <strong>💡 Pro Tip:</strong> Use Collection Runner's "Keep variable values" option when debugging—it preserves any changes made during the run, making it easier to inspect intermediate states!
</div>

Now you're ready to organize your API testing like a pro! 📁
        `,
        exercise: {
          title: "Build a Complete Collection",
          description:
            "Create a collection that tests a REST API with authentication, CRUD operations, and error handling.",
          starterCode: `// Collection structure required:
// - Setup folder with login request
// - CRUD folder with all operations
// - Edge Cases folder for error testing
// - Cleanup folder to delete test data
// Include collection variables, tests, and documentation`,
        },
      },
      {
        id: "variables",
        title: "Postman Variables: Dynamic Values Everywhere",
        estimatedMinutes: 15,
        content: `
# Postman Variables: The Key to Dynamic Requests

Variables are placeholders that store and reuse values across your requests. They're essential for maintaining testable, maintainable API collections.

## Variable Scopes in Postman

Postman has FIVE variable scopes, each with different precedence:

| Scope | Lifetime | Access | Use Case |
| :--- | :--- | :--- | :--- |
| **Global** | Until deleted | All collections, all environments | Shared across all work |
| **Collection** | Collection lifetime | Within collection | Collection-specific values |
| **Environment** | Active environment | Within environment | Environment-specific config |
| **Data** | Collection run only | During runner iteration | Data-driven testing |
| **Local** | Request/script only | Within script | Temporary values |

### Precedence (Highest to Lowest):
1. Local (temporary within script)
2. Data (from CSV/JSON during Runner)
3. Environment (active environment)
4. Collection (collection variables)
5. Global (across all collections)

## Creating and Managing Variables

### 1. **Global Variables**
**Use when:** Values shared across ALL your work

**Setup:**
1. Click "Environments" → "Globals"
2. Add variables:
   - \`company_name\`: "Acme Inc"
   - \`support_email\`: "support@acme.com"
   - \`version\`: "2.0"

**Access:** \`{{company_name}}\`

### 2. **Environment Variables**
**Use when:** Different values for dev/staging/production

**Create environment:**
1. Click "Environments" → "Create Environment"
2. Name: "Development"
3. Add variables:
   - \`base_url\`: "https://dev-api.example.com"
   - \`api_key\`: "dev_key_123"
   - \`timeout\`: "5000"

**Create Production environment:**
- \`base_url\`: "https://api.example.com"
- \`api_key\`: "prod_key_xyz"
- \`timeout\`: "10000"

**Usage:** Switch environments from dropdown!

### 3. **Collection Variables**
**Use when:** Values specific to a collection

**Setup:**
1. Click collection → Variables tab
2. Add variables:
   - \`collection_name\`: "User API Tests"
   - \`version\`: "1.0.0"
   - \`max_retries\`: "3"

### 4. **Local Variables**
**Use when:** Temporary values within a script

\`\`\`javascript
// Pre-request or Test script
let tempId = Math.random().toString(36);
let timestamp = Date.now();
let isAuthenticated = true;

// Can't use {{tempId}} in URL, only in script!
console.log(tempId);
\`\`\`

## Variable Types

### 1. **String Variables**
\`\`\`
"Hello World"
"{{base_url}}/users"  // Can contain other variables
\`\`\`

### 2. **Number Variables**
\`\`\`
1000
3.14159
-42
\`\`\`

### 3. **Boolean Variables**
\`\`\`
true
false
\`\`\`

### 4. **Object Variables (JSON)**
\`\`\`json
{
  "name": "John",
  "email": "john@example.com"
}
\`\`\`

**Access in scripts:**
\`\`\`javascript
let user = pm.variables.get("user_object");
let parsed = JSON.parse(user);
console.log(parsed.name); // "John"
\`\`\`

## Using Variables in Different Places

### In URLs:
\`\`\`http
GET {{base_url}}/users/{{user_id}}
POST {{base_url}}/api/{{version}}/products
\`\`\`

### In Headers:
\`\`\`
Authorization: Bearer {{auth_token}}
X-API-Key: {{api_key}}
X-Request-ID: {{$guid}}
\`\`\`

### In Request Body:
\`\`\`json
{
  "user_id": "{{user_id}}",
  "timestamp": "{{$timestamp}}",
  "name": "{{user_name}}"
}
\`\`\`

### In Tests:
\`\`\`javascript
// Set variables from response
let jsonData = pm.response.json();
pm.environment.set("user_id", jsonData.id);
pm.collectionVariables.set("auth_token", jsonData.token);

// Get variables
let userId = pm.variables.get("user_id");
console.log(\`Created user ID: \${userId}\`);
\`\`\`

## Dynamic Variables (Built-in)

Postman provides built-in dynamic variables:

| Variable | Output | Example |
| :--- | :--- | :--- |
| \`{{$guid}}\` | Random GUID | "9c5b94b1-5ad5-4c3d-8b4d-5e5f5e5f5e5f" |
| \`{{$timestamp}}\` | Current timestamp | 1699123456789 |
| \`{{$randomInt}}\` | Random integer (0-1000) | 427 |
| \`{{$randomFirstName}}\` | Random first name | "Emma" |
| \`{{$randomLastName}}\` | Random last name | "Johnson" |
| \`{{$randomEmail}}\` | Random email | "emma.johnson@example.com" |
| \`{{$randomPhoneNumber}}\` | Random phone | "1-555-123-4567" |
| \`{{$randomColor}}\` | Random color | "blue" |
| \`{{$randomBoolean}}\` | Random boolean | true |
| \`{{$randomUUID}}\` | Random UUID | "f47ac10b-58cc-4372-a567-0e02b2c3d479" |
| \`{{$randomAbbreviation}}\` | Random abbreviation | "ABC" |

## Practical Variable Patterns

### Pattern 1: Authentication Flow

**Request 1: Login**
\`\`\`javascript
// Tests
let jsonData = pm.response.json();
pm.environment.set("access_token", jsonData.token);
pm.environment.set("refresh_token", jsonData.refreshToken);
pm.environment.set("user_id", jsonData.user.id);
\`\`\`

**Request 2: Use Token**
\`\`\`http
GET {{base_url}}/users/{{user_id}}
Authorization: Bearer {{access_token}}
\`\`\`

### Pattern 2: CRUD Operations

**Create resource:**
\`\`\`javascript
// Save ID for later
let newId = pm.response.json().id;
pm.collectionVariables.set("created_id", newId);
\`\`\`

**Get resource:**
\`\`\`http
GET {{base_url}}/items/{{created_id}}
\`\`\`

**Update resource:**
\`\`\`http
PUT {{base_url}}/items/{{created_id}}
\`\`\`

**Delete resource:**
\`\`\`http
DELETE {{base_url}}/items/{{created_id}}
\`\`\`

### Pattern 3: Pagination

\`\`\`javascript
// Pre-request script
let page = pm.collectionVariables.get("current_page") || 1;
pm.variables.set("page", page);
pm.variables.set("limit", 20);

// Test script
let response = pm.response.json();
if (response.has_more) {
    let nextPage = page + 1;
    pm.collectionVariables.set("current_page", nextPage);
    // Could trigger next request
}
\`\`\`

### Pattern 4: Dynamic Test Data

\`\`\`json
{
  "email": "{{$randomEmail}}",
  "first_name": "{{$randomFirstName}}",
  "last_name": "{{$randomLastName}}",
  "phone": "{{$randomPhoneNumber}}",
  "registration_date": "{{$timestamp}}"
}
\`\`\`

## Working with Variables in Scripts

### pm.variables API:

\`\`\`javascript
// Get variable (checks all scopes)
let value = pm.variables.get("var_name");

// Set in specific scope
pm.globals.set("key", "value");
pm.environment.set("key", "value");
pm.collectionVariables.set("key", "value");

// Get from specific scope
let global = pm.globals.get("key");
let env = pm.environment.get("key");
let collection = pm.collectionVariables.get("key");

// Check if exists
let exists = pm.variables.has("var_name");

// Unset/delete
pm.environment.unset("key");

// Clear all in scope
pm.environment.clear();
pm.globals.clear();

// Get all variables (object)
let allVars = pm.variables.toObject();
\`\`\`

### Variable Manipulation:

\`\`\`javascript
// Increment counter
let counter = pm.environment.get("request_counter") || 0;
counter++;
pm.environment.set("request_counter", counter);

// Store array as JSON
let history = pm.environment.get("request_history") || [];
history.push({
    url: pm.request.url.toString(),
    timestamp: Date.now()
});
pm.environment.set("request_history", JSON.stringify(history));

// Template strings with variables
let message = \`Request \${counter} completed at \${new Date().toISOString()}\`;
pm.environment.set("last_message", message);
\`\`\`

## Secrets Management

### Never hardcode secrets!

**❌ BAD:**
\`\`\`javascript
pm.environment.set("api_key", "abc123xyz789");
pm.request.headers.add({
    key: "X-API-Key",
    value: "abc123xyz789"
});
\`\`\`

**✅ GOOD:**
1. Create environment with empty placeholder:
   - Key: \`api_key\`
   - Value: \`\`
   - Type: secret

2. Set via secure method:
\`\`\`javascript
// During login/auth flow
pm.environment.set("api_key", response.json().apiKey);
\`\`\`

3. Use variable:
\`\`\`http
X-API-Key: {{api_key}}
\`\`\`

### Secret Variable Type
In Postman Pro/Enterprise:
1. Mark variable as "Secret"
2. Value is masked in UI (••••••)
3. Never appears in logs/exports
4. Can't be viewed after setting

## Troubleshooting Variables

### Variables not working? Check:

1. **Scope precedence:** Local > Data > Environment > Collection > Global
2. **Spelling:** Variable names are case-sensitive
3. **Whitespace:** \`{{ api_key }}\` won't work (no spaces!)
4. **Initialization:** Variable must exist before use
5. **Scope visibility:** Local variables aren't accessible in URLs

### Debugging variables:

\`\`\`javascript
// Log all variables
console.log("Global:", pm.globals.toObject());
console.log("Environment:", pm.environment.toObject());
console.log("Collection:", pm.collectionVariables.toObject());
console.log("Variables:", pm.variables.toObject());

// Log specific variable
console.log("API Key exists:", pm.variables.has("api_key"));
console.log("API Key value:", pm.variables.get("api_key"));
\`\`\`

## Best Practices

### ✅ **DO:**
- Use descriptive variable names (\`user_id\` not \`id\`)
- Initialize variables before use
- Use appropriate scope (global only when necessary)
- Clean up temporary variables
- Use secrets for credentials
- Document important variables
- Use environment for config differences

### ❌ **DON'T:**
- Don't hardcode values that change
- Don't use global for everything
- Don't store sensitive data in collections
- Don't rely on local variables outside scripts
- Don't forget to unset temporary variables

## Hands-On Exercises

### Beginner Exercise:
Create environment for API testing:
1. Create "Dev" environment with \`base_url\` = \`https://dev.api.com\`
2. Create "Prod" environment with \`base_url\` = \`https://api.com\`
3. Create request using \`{{base_url}}/users\`
4. Switch between environments

### Intermediate Exercise:
Build authentication variable flow:
1. POST /login request saves \`token\` to environment
2. GET /profile uses \`{{token}}\` in Authorization header
3. Create pre-request script that refreshes token if expired
4. Add test that verifies token exists

### Advanced Exercise:
Create data-driven test with variables:
1. Create CSV file with test data (username, email, role)
2. Write pre-request script to load data
3. POST request uses data variables
4. Collection runner uses iterations
5. Save responses to environment variables

<div style="padding: 15px; background-color: #F0FDF4; border-left: 4px solid #22C55E; margin: 20px 0; border-radius: 4px;">
  <strong>💡 Pro Tip:</strong> Use the Variables sidebar (click "eye" icon top right) to see all current variable values across scopes in real-time during debugging!
</div>

Now you're a Postman variables expert! 🔧
        `,
        exercise: {
          title: "Dynamic API Testing Suite",
          description:
            "Create a complete testing suite using variables for different environments, authentication, and test data.",
          starterCode: `// Requirements:
// 1. Dev/Staging/Prod environments
// 2. Authentication token management
// 3. Dynamic test data using built-in variables
// 4. Collection variables for test state
// 5. Automatic cleanup of test data`,
        },
      },
    ],
  },
  {
    id: "testing-automation",
    title: "4. Automated Testing",
    summary:
      "Write robust tests, automate API validation, and integrate with CI/CD pipelines.",
    lessons: [
      {
        id: "test-scripts",
        title: "Writing Test Scripts: The pm API",
        estimatedMinutes: 20,
        content: `
# Writing Test Scripts: The Complete pm API Guide

Postman's testing framework is built on the \`pm\` (Postman) API, a powerful library for validating API responses and controlling request flow.

## The pm API Overview

The \`pm\` object provides everything you need for testing:

\`\`\`javascript
pm.info          // Request metadata
pm.request       // Current request details
pm.response      // Response data and methods  
pm.variables     // Variable management
pm.environment   // Environment variables
pm.globals       // Global variables  
pm.collectionVariables // Collection variables
pm.sendRequest() // Make HTTP requests
pm.expect()      // Assertion library (Chai.js)
pm.test()        // Define a test
\`\`\`

## Core Testing Methods

### pm.test() - Define Individual Tests

\`\`\`javascript
// Basic test
pm.test("Status code is 200", function () {
    pm.response.to.have.status(200);
});

// Test with async callback
pm.test("Response time is fast", function (done) {
    setTimeout(() => {
        pm.expect(pm.response.responseTime).to.be.below(500);
        done();
    }, 100);
});

// Test without callback (synchronous)
pm.test("Response has JSON body", () => {
    pm.response.to.be.json();
});
\`\`\`

### pm.expect() - Make Assertions

Uses Chai.js BDD syntax:

\`\`\`javascript
// Equality
pm.expect(5).to.equal(5);
pm.expect("hello").to.equal("hello");
pm.expect(response.id).to.equal(123);

// Inclusion
pm.expect([1,2,3]).to.include(2);
pm.expect("Hello World").to.contain("World");

// Types
pm.expect(response).to.be.an('object');
pm.expect(response.id).to.be.a('number');
pm.expect(response.name).to.be.a('string');

// Truthiness
pm.expect(response).to.exist;
pm.expect(response.error).to.be.false;
pm.expect(response.data).to.not.be.null;

// Length
pm.expect(response.items).to.have.lengthOf(10);
pm.expect(response.name).to.have.length.below(50);

// Deep equality (objects/arrays)
pm.expect(response.user).to.deep.equal({
    id: 1,
    name: "John"
});

// Numeric comparisons
pm.expect(response.price).to.be.above(0);
pm.expect(response.score).to.be.at.least(100);
pm.expect(response.time).to.be.below(1000);
pm.expect(response.rating).to.be.within(1, 5);

// Regular expressions
pm.expect(response.email).to.match(/^[^@]+@[^@]+$/);
\`\`\`

## Response Testing

### HTTP Status Codes

\`\`\`javascript
// Single status
pm.test("Status is 200", () => {
    pm.response.to.have.status(200);
});

// Multiple possible statuses
pm.test("Successful status", () => {
    pm.expect(pm.response.code).to.be.oneOf([200, 201, 202]);
});

// Client/server error detection
pm.test("No client error", () => {
    pm.expect(pm.response.code).to.be.below(400);
});
\`\`\`

### Response Headers

\`\`\`javascript
pm.test("Content-Type is JSON", () => {
    pm.response.to.have.header("Content-Type");
    pm.expect(pm.response.headers.get("Content-Type"))
        .to.include("application/json");
});

pm.test("Rate limit headers exist", () => {
    pm.expect(pm.response.headers.has("X-RateLimit-Limit")).to.be.true;
    pm.expect(pm.response.headers.has("X-RateLimit-Remaining")).to.be.true;
});

// Get and store header value
let etag = pm.response.headers.get("ETag");
pm.environment.set("last_etag", etag);
\`\`\`

### Response Body - JSON

\`\`\`javascript
// Parse JSON
let jsonData = pm.response.json();

// Basic property checks
pm.test("User has required fields", () => {
    pm.expect(jsonData).to.have.all.keys('id', 'name', 'email');
});

// Value validation
pm.test("User data is valid", () => {
    pm.expect(jsonData.id).to.be.a('number');
    pm.expect(jsonData.name).to.not.be.empty;
    pm.expect(jsonData.email).to.include('@');
});

// Nested properties
pm.test("Address is complete", () => {
    pm.expect(jsonData.address).to.have.property('street');
    pm.expect(jsonData.address).to.have.property('city');
    pm.expect(jsonData.address.city).to.match(/^[A-Z]/);
});

// Array validation
pm.test("Items array valid", () => {
    pm.expect(jsonData.items).to.be.an('array');
    pm.expect(jsonData.items.length).to.be.at.least(1);
    
    jsonData.items.forEach(item => {
        pm.expect(item).to.have.property('id');
        pm.expect(item).to.have.property('name');
    });
});

// Schema validation (simple)
pm.test("Response matches schema", () => {
    const schema = {
        type: "object",
        required: ["id", "name"],
        properties: {
            id: { type: "number" },
            name: { type: "string" }
        }
    };
    
    // You'd need a JSON schema validator library here
    // pm.expect(tv4.validate(jsonData, schema)).to.be.true;
});
\`\`\`

### Response Body - Other Formats

\`\`\`javascript
// XML response
let xmlData = pm.response.text(); // Raw XML string
// You'd need an XML parser for detailed validation

// HTML response
let html = pm.response.text();
pm.test("Page contains title", () => {
    pm.expect(html).to.include("<title>My Page</title>");
});

// Plain text
let text = pm.response.text();
pm.test("Response contains expected text", () => {
    pm.expect(text).to.include("Success");
    pm.expect(text).to.match(/Transaction ID: \\w+/);
});
\`\`\`

## Performance Testing

### Response Time

\`\`\`javascript
pm.test("Response time is acceptable", () => {
    pm.expect(pm.response.responseTime).to.be.below(1000); // 1 second
});

// Different thresholds for different endpoints
let threshold = pm.request.url.path.includes("search") ? 500 : 2000;
pm.test(\`Response time < \${threshold}ms\`, () => {
    pm.expect(pm.response.responseTime).to.be.below(threshold);
});

// Store for trending analysis
let responseTime = pm.response.responseTime;
let history = pm.environment.get("response_times") || [];
history.push(responseTime);
pm.environment.set("response_times", JSON.stringify(history));
\`\`\`

## Request/Response Data Extraction

### Setting Variables from Response

\`\`\`javascript
let jsonData = pm.response.json();

// Simple assignment
pm.environment.set("user_id", jsonData.id);
pm.collectionVariables.set("auth_token", jsonData.token);

// Extracting nested data
let firstItemId = jsonData.items[0].id;
pm.variables.set("first_item_id", firstItemId);

// Extracting from array
let userIds = jsonData.users.map(user => user.id);
pm.environment.set("user_ids", JSON.stringify(userIds));

// Extracting with regex from headers
let linkHeader = pm.response.headers.get("Link");
let nextPageMatch = linkHeader.match(/<([^>]+)>; rel="next"/);
if (nextPageMatch) {
    pm.environment.set("next_page_url", nextPageMatch[1]);
}
\`\`\`

### Accessing Request Data

\`\`\`javascript
// Request metadata
console.log(pm.info.requestName);
console.log(pm.info.requestId);

// Request URL and method
console.log(pm.request.url.toString());
console.log(pm.request.method);

// Request headers
let authHeader = pm.request.headers.get("Authorization");
console.log("Auth header present:", !!authHeader);

// Request body
if (pm.request.body) {
    let bodyText = pm.request.body.toString();
    console.log("Request body:", bodyText);
}
\`\`\`

## Pre-request Scripts

Run BEFORE request is sent:

\`\`\`javascript
// Generate dynamic data
const timestamp = Date.now();
pm.variables.set("timestamp", timestamp);
pm.variables.set("nonce", Math.random().toString(36));

// Add headers dynamically
pm.request.headers.add({
    key: "X-Timestamp",
    value: timestamp.toString()
});

// Modify request body
if (pm.request.body && pm.request.body.mode === "raw") {
    let body = JSON.parse(pm.request.body.raw);
    body.timestamp = timestamp;
    pm.request.body.raw = JSON.stringify(body);
}

// Conditional logic
if (pm.environment.get("env") === "production") {
    pm.request.headers.upsert({
        key: "X-Production-Only",
        value: "true"
    });
}
\`\`\`

## Advanced Testing Patterns

### 1. **Chained Requests**

\`\`\`javascript
// In Tests tab
pm.test("Create and verify user", () => {
    let userId = pm.response.json().id;
    
    // Make second request
    pm.sendRequest({
        url: \`https://api.example.com/users/\${userId}\`,
        method: 'GET',
        header: {
            'Authorization': 'Bearer ' + pm.environment.get("token")
        }
    }, (err, res) => {
        pm.test("Retrieved user matches", () => {
            pm.expect(res.json().id).to.equal(userId);
        });
    });
});
\`\`\`

### 2. **Data-Driven Tests**

\`\`\`javascript
// Using collection/data variables
let testCases = pm.iterationData.toObject(); // From CSV/JSON

pm.test(\`User \${testCases.username} creation\`, () => {
    let response = pm.response.json();
    pm.expect(response.username).to.equal(testCases.username);
    pm.expect(response.email).to.equal(testCases.email);
    pm.expect(response.role).to.equal(testCases.expected_role);
});
\`\`\`

### 3. **Looping and Conditions**

\`\`\`javascript
// Pagination loop
let hasMore = pm.response.json().has_more;
let page = pm.environment.get("page") || 1;

pm.test("Process all pages", () => {
    if (hasMore && page < 10) {
        pm.environment.set("page", page + 1);
        // Trigger next request
        postman.setNextRequest(pm.info.requestName);
    } else {
        postman.setNextRequest(null); // Stop
    }
});
\`\`\`

### 4. **Error Handling**

\`\`\`javascript
pm.test("Handle API errors gracefully", () => {
    if (pm.response.code === 404) {
        console.log("Resource not found, skipping validation");
        pm.expect(true).to.be.true;
        return;
    }
    
    if (pm.response.code === 429) {
        let retryAfter = pm.response.headers.get("Retry-After");
        console.log(\`Rate limited, retry after \${retryAfter}s\`);
        pm.expect(false).to.be.false; // Fail test
        return;
    }
    
    // Normal validation
    pm.response.to.have.status(200);
    let jsonData = pm.response.json();
    pm.expect(jsonData).to.have.property('data');
});
\`\`\`

## Test Organization

### Folder Structure for Tests

\`\`\`
Collection
  ├── 📁 Setup
  │   └── Pre-request: Get Auth Token
  ├── 📁 Positive Tests  
  │   ├── Test: Valid user creation
  │   ├── Test: Valid data retrieval
  │   └── Test: Valid update
  ├── 📁 Negative Tests
  │   ├── Test: Missing required fields
  │   ├── Test: Invalid authentication
  │   └── Test: Resource not found
  ├── 📁 Edge Cases
  │   ├── Test: Empty response
  │   ├── Test: Large payload
  │   └── Test: Special characters
  └── 📁 Cleanup
      └── Test: Delete test data
\`\`\`

### Test Naming Best Practices

\`\`\`javascript
// Good: Descriptive and specific
pm.test("GET /users/:id returns 200 for existing user", ...);
pm.test("Response body contains all required fields", ...);
pm.test("Email field matches regex pattern for valid emails", ...);

// Bad: Vague and unhelpful
pm.test("Test 1", ...);
pm.test("It works", ...);
pm.test("Check", ...);
\`\`\`

## Console Logging for Debugging

\`\`\`javascript
// Log with context
console.log("=== Request ===");
console.log("URL:", pm.request.url);
console.log("Method:", pm.request.method);
console.log("Headers:", pm.request.headers);

console.log("=== Response ===");
console.log("Status:", pm.response.code);
console.log("Time:", pm.response.responseTime + "ms");
console.log("Body:", pm.response.json());

// Conditional logging
if (pm.response.responseTime > 500) {
    console.warn("Slow response detected!");
}

// Log to Postman Console only
console.info("This appears in View → Show Postman Console");
\`\`\`

## Complete Test Suite Example

\`\`\`javascript
// Comprehensive test suite for User API

// 1. Response validation
pm.test("Response status is 200 OK", () => {
    pm.response.to.have.status(200);
});

pm.test("Response time is under 500ms", () => {
    pm.expect(pm.response.responseTime).to.be.below(500);
});

// 2. Header validation  
pm.test("Content-Type is application/json", () => {
    pm.response.to.have.header("Content-Type");
    pm.expect(pm.response.headers.get("Content-Type"))
        .to.equal("application/json; charset=utf-8");
});

pm.test("Rate limit headers present", () => {
    let headers = ["X-RateLimit-Limit", "X-RateLimit-Remaining", "X-RateLimit-Reset"];
    headers.forEach(header => {
        pm.expect(pm.response.headers.has(header)).to.be.true;
    });
});

// 3. Body structure validation
let jsonData = pm.response.json();

pm.test("Response has expected properties", () => {
    pm.expect(jsonData).to.have.all.keys("data", "meta", "links");
});

pm.test("Data array is not empty", () => {
    pm.expect(jsonData.data).to.be.an("array");
    pm.expect(jsonData.data.length).to.be.at.least(1);
});

// 4. Content validation
let firstItem = jsonData.data[0];

pm.test("Each item has required fields", () => {
    const requiredFields = ["id", "type", "attributes"];
    requiredFields.forEach(field => {
        pm.expect(firstItem).to.have.property(field);
    });
});

pm.test("Item attributes are valid", () => {
    pm.expect(firstItem.attributes.name).to.be.a("string");
    pm.expect(firstItem.attributes.name).to.not.be.empty;
    pm.expect(firstItem.attributes.created_at).to.match(/^\\d{4}-\\d{2}-\\d{2}/);
});

// 5. Extract and store data for future tests
let userId = firstItem.id;
pm.environment.set("test_user_id", userId);
pm.environment.set("test_user_name", firstItem.attributes.name);

console.log(\`Test data saved - User ID: \${userId}\`);
\`\`\`

## Best Practices

### ✅ **DO:**
- Write descriptive test names
- Test both positive and negative scenarios
- Validate response structure and content
- Store values from responses for chaining
- Use environment variables for dynamic data
- Keep tests independent (no order dependency)
- Log useful debugging information
- Test edge cases and error conditions

### ❌ **DON'T:**
- Don't hardcode expected values that change
- Don't write tests that depend on execution order
- Don't ignore failed assertions
- Don't log sensitive data (passwords, tokens)
- Don't make tests too complex (keep focused)
- Don't forget to handle async operations

## Hands-On Exercises

### Beginner Exercise:
Write tests for a GET endpoint:
1. Check status code is 200
2. Verify response time < 1 second
3. Check Content-Type header
4. Validate response has expected fields

### Intermediate Exercise:
Build test suite for CRUD API:
1. POST: Test creation returns 201 and ID
2. GET: Test retrieval matches created data
3. PUT: Test update changes values
4. DELETE: Test deletion returns 204
5. GET: Verify 404 after deletion

### Advanced Exercise:
Create automated test workflow:
1. Pre-request script generates test data
2. Chain multiple requests with extracted data
3. Add pagination testing with loops
4. Implement retry logic for flaky tests
5. Generate test report with summary

<div style="padding: 15px; background-color: #F0FDF4; border-left: 4px solid #22C55E; margin: 20px 0; border-radius: 4px;">
  <strong>💡 Pro Tip:</strong> Use Postman Console (View → Show Postman Console) to debug tests. It shows console.log output, request/response details, and any JavaScript errors!
</div>

Now you're ready to write professional API tests! 🧪
        `,
        quiz: [
          {
            question:
              "What method do you use to access the response body as a JavaScript object?",
            options: [
              "pm.response.text()",
              "pm.response.body()",
              "pm.response.json()",
              "pm.response.parse()",
            ],
            correctAnswer: 2,
            explanation:
              "pm.response.json() parses the response body as JSON and returns a JavaScript object. text() returns raw string, body() and parse() don't exist in the pm API.",
          },
        ],
      },
      {
        id: "collection-runner",
        title: "Collection Runner & Newman CLI",
        estimatedMinutes: 15,
        content: `
# Collection Runner & Newman CLI: Automating API Tests

Running individual requests manually is fine for development, but for regression testing and CI/CD, you need automation. Postman provides two powerful tools: Collection Runner (GUI) and Newman (CLI).

## Collection Runner (Postman GUI)

The built-in Collection Runner executes multiple requests in sequence with various configurations.

### How to Launch
1. Click "Runner" button (top left)
2. Select your collection
3. Configure run settings
4. Click "Run"

### Runner Configuration Options

| Setting | Description | Use Case |
| :--- | :--- | :--- |
| **Environment** | Choose environment variables | Testing in different contexts |
| **Iterations** | Number of times to run collection | Data-driven testing, load testing |
| **Delay** | Wait time between requests | Simulate real user behavior |
| **Data File** | CSV/JSON file with test data | Parameterized testing |
| **Keep Variable Values** | Persist variable changes | Debugging, subsequent runs |
| **Save Responses** | Store responses to files | Logging, debugging |
| **Run in Parallel** | Execute requests concurrently | Performance testing (Pro) |

### Running Collections

**Basic Run:**
1. Select collection
2. Choose environment
3. Click "Run"

**Data-Driven Run:**
1. Prepare CSV:
\`\`\`csv
username,email,role
john,john@test.com,admin
jane,jane@test.com,user
bob,bob@test.com,viewer
\`\`\`
2. Upload data file
3. Set iterations = 3
4. Run

**Load Testing:**
1. Set iterations = 100
2. Set delay = 0
3. Run to see performance patterns

### Runner Results

After running, you get:
- **Pass/Fail Summary:** Number of passed/failed tests
- **Request Timeline:** Execution order and duration
- **Test Results:** Details of each test assertion
- **Response Previews:** Click any request to see response

**Export Results:**
- JSON format
- HTML report
- JUnit XML (for CI/CD)

## Newman: Command-Line Runner

Newman is Postman's command-line companion that runs collections from terminal/CI/CD.

### Installation

\`\`\`bash
# Install globally
npm install -g newman

# Verify installation
newman --version

# Update newman
npm update -g newman
\`\`\`

### Basic Usage

\`\`\`bash
# Run collection from file
newman run MyCollection.json

# Run collection from URL
newman run https://www.getpostman.com/collections/abc123

# With environment
newman run MyCollection.json -e MyEnvironment.json

# With global variables
newman run MyCollection.json -g MyGlobals.json

# With data file (CSV/JSON)
newman run MyCollection.json -d data.csv

# Specify number of iterations
newman run MyCollection.json -n 10

# Add delay between requests (ms)
newman run MyCollection.json --delay-request 1000
\`\`\`

### Advanced Newman Options

**Folder filtering:**
\`\`\`bash
# Run specific folder only
newman run MyCollection.json --folder "Authentication Tests"

# Run multiple folders
newman run MyCollection.json --folder "Auth Tests" --folder "CRUD Tests"

# Skip specific folders
newman run MyCollection.json --skip-folder "Performance Tests"
\`\`\`

**Request filtering:**
\`\`\`bash
# Run specific requests by ID
newman run MyCollection.json --include-ids "abc123,def456"

# Skip specific requests
newman run MyCollection.json --skip-ids "xyz789"
\`\`\`

**Timeout configuration:**
\`\`\`bash
# Request timeout (ms)
newman run MyCollection.json --timeout-request 5000

# Script timeout
newman run MyCollection.json --timeout-script 3000

# Global timeout
newman run MyCollection.json --timeout 10000
\`\`\`

**SSL and Security:**
\`\`\`bash
# Disable SSL verification (not recommended)
newman run MyCollection.json --insecure

# Use custom CA certificate
newman run MyCollection.json --ssl-client-cert-list client-cert.json
\`\`\`

### Newman Reporters

Newman supports multiple output formats:

**Built-in reporters:**
\`\`\`bash
# CLI output (default)
newman run MyCollection.json -r cli

# JSON report
newman run MyCollection.json -r json --reporter-json-export results.json

# JUnit XML (for Jenkins, GitLab CI)
newman run MyCollection.json -r junit --reporter-junit-export results.xml

# HTML report (3rd party)
npm install -g newman-reporter-html
newman run MyCollection.json -r html --reporter-html-export results.html

# Multiple reporters
newman run MyCollection.json -r cli,json,junit
\`\`\`

**Custom output directory:**
\`\`\`bash
newman run MyCollection.json --export-environment new_env.json
newman run MyCollection.json --export-globals new_globals.json
newman run MyCollection.json --export-collection updated_collection.json
\`\`\`

## CI/CD Integration Examples

### Jenkins Pipeline

\`\`\`groovy
pipeline {
    agent any
    stages {
        stage('API Tests') {
            steps {
                sh '''
                    npm install -g newman
                    newman run tests/API-Tests.json \
                        -e environments/jenkins-env.json \
                        -r junit \
                        --reporter-junit-export test-results.xml
                '''
            }
        }
    }
    post {
        always {
            junit 'test-results.xml'
        }
    }
}
\`\`\`

### GitLab CI

\`\`\`yaml
api-tests:
  stage: test
  script:
    - npm install -g newman
    - newman run collections/API-Tests.json
        -e environments/$CI_ENVIRONMENT_NAME.json
        -r junit
        --reporter-junit-export report.xml
  artifacts:
    reports:
      junit: report.xml
    paths:
      - report.xml
  only:
    - merge_requests
    - main
\`\`\`

### GitHub Actions

\`\`\`yaml
name: API Tests
on: [push, pull_request]

jobs:
  test:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v2
      
      - name: Install Newman
        run: npm install -g newman
      
      - name: Run API Tests
        run: |
          newman run collections/API-Tests.json \
            -e environments/github-actions.json \
            -r cli,json \
            --reporter-json-export results.json
      
      - name: Upload test results
        uses: actions/upload-artifact@v2
        if: always()
        with:
          name: test-results
          path: results.json
\`\`\`

### CircleCI

\`\`\`yaml
version: 2.1
jobs:
  api-test:
    docker:
      - image: postman/newman:latest
    steps:
      - checkout
      - run:
          name: Run API Tests
          command: |
            newman run collections/API-Tests.json \
              -e environments/circleci-env.json \
              -r junit \
              --reporter-junit-export test-results.xml
      - store_test_results:
          path: test-results.xml

workflows:
  test:
    jobs:
      - api-test
\`\`\`

### Azure DevOps

\`\`\`yaml
trigger:
- main

pool:
  vmImage: 'ubuntu-latest'

steps:
- script: |
    npm install -g newman
    newman run collections/API-Tests.json \
      -e environments/azure-devops-env.json \
      -r junit \
      --reporter-junit-export $(Build.ArtifactStagingDirectory)/test-results.xml
  displayName: 'Run API Tests'

- task: PublishTestResults@2
  inputs:
    testResultsFormat: 'JUnit'
    testResultsFiles: '**/test-results.xml'
  condition: succeededOrFailed()
\`\`\`

## Advanced Newman Features

### Custom Scripts with Newman

\`\`\`javascript
// newman-run.js
const newman = require('newman');

newman.run({
    collection: './collections/API-Tests.json',
    environment: './environments/dev.json',
    iterationData: './data/test-data.csv',
    globals: './globals.json',
    reporters: ['cli', 'json', 'junit'],
    reporter: {
        json: { export: './reports/results.json' },
        junit: { export: './reports/junit.xml' }
    },
    timeout: 10000,
    delayRequest: 1000,
    bail: true, // Stop on first failure
    suppressExitCode: false // Exit with non-zero on failure
}, function (err, summary) {
    if (err) {
        console.error('Collection run failed:', err);
        process.exit(1);
    }
    
    console.log('Collection run completed!');
    console.log('Total requests:', summary.run.stats.requests.total);
    console.log('Failed tests:', summary.run.stats.assertions.failed);
    
    if (summary.run.stats.assertions.failed > 0) {
        process.exit(1);
    }
});
\`\`\`

Run with: \`node newman-run.js\`

### Parallel Execution (Newman +)

\`\`\`bash
# Install newman-parallel
npm install -g newman-parallel

# Run multiple collections in parallel
parallel-newman \
  -c collections/auth.json \
  -c collections/users.json \
  -c collections/products.json \
  -e environments/dev.json
\`\`\`

## Best Practices for Collection Runner/Newman

### ✅ **DO:**
- Use environment files for different configurations
- Keep collections version-controlled
- Generate reports for CI/CD integration
- Set appropriate timeouts for your API
- Use data files for parameterized tests
- Implement retry logic for flaky tests
- Store artifacts (reports, logs) in CI/CD
- Run tests in isolated environment

### ❌ **DON'T:**
- Don't hardcode sensitive data in collections
- Don't run destructive tests on production
- Don't ignore test failures (exit non-zero)
- Don't run without proper timeouts
- Don't store large response files in Git

## Troubleshooting Common Issues

### Newman can't find collection:
\`\`\`bash
# Use absolute path or check relative path
newman run /absolute/path/to/collection.json
newman run ./relative/path/collection.json
\`\`\`

### Environment variables not loading:
\`\`\`bash
# Verify file is valid JSON
cat environment.json | jq

# Check variable substitution
newman run collection.json -e env.json --verbose
\`\`\`

### SSL Certificate errors:
\`\`\`bash
# Development only - disable SSL verification
newman run collection.json --insecure

# Production - use proper certificates
newman run collection.json --ssl-client-cert-list certs.json
\`\`\`

## Hands-On Exercises

### Beginner Exercise:
1. Export a collection as JSON
2. Run it with Newman in terminal
3. Generate JSON report
4. View report contents

### Intermediate Exercise:
1. Create CSV data file for user creation
2. Run collection with data-driven iterations
3. Generate JUnit XML report
4. Integrate into GitHub Actions

### Advanced Exercise:
1. Set up Jenkins pipeline
2. Run collections on PR creation
3. Parse results and comment on PR
4. Deploy only if tests pass

<div style="padding: 15px; background-color: #F0FDF4; border-left: 4px solid #22C55E; margin: 20px 0; border-radius: 4px;">
  <strong>💡 Pro Tip:</strong> Use Newman's \`--bail\` flag in CI/CD to stop execution at first failure—saves time and resources during automated testing!
</div>

Now you can automate your API tests anywhere! 🤖
        `,
        exercise: {
          title: "CI/CD Pipeline Integration",
          description:
            "Set up automated API testing in your CI/CD pipeline using Newman.",
          starterCode: `// Create:
// 1. Collection with at least 5 requests
// 2. Environment file for CI
// 3. Data file for parameterized tests
// 4. CI configuration (GitHub Actions/Jenkins/GitLab CI)
// 5. Report generation and artifact storage`,
        },
      },
    ],
  },
  {
    id: "advanced",
    title: "5. Advanced Techniques",
    summary:
      "Master mocks, monitors, workflows, and integrations for enterprise-grade API testing.",
    lessons: [
      {
        id: "mock-servers",
        title: "Mock Servers: Simulating APIs",
        estimatedMinutes: 12,
        content: `
# Mock Servers: Simulating APIs for Parallel Development

Mock servers allow you to simulate API responses before the actual backend is built. This enables frontend and backend teams to work in parallel.

## What Are Mock Servers?

A mock server is a simulated API endpoint that returns predefined responses based on:
- Request path
- HTTP method
- Request body (optional)
- Query parameters (optional)

**Benefits:**
- Frontend development can start immediately
- Contract testing between teams
- Demo APIs without backend
- Isolated testing of edge cases

## Creating a Mock Server

### Method 1: From Collection

1. Select collection
2. Click "..." → "Mock Collection"
3. Configure:
   - Name: "User API Mock"
   - Environment: Select or create
   - Response delay: 0-10000ms
4. Click "Create Mock Server"

### Method 2: From Scratch

1. Click "New" → "Mock Server"
2. Name: "Product API Mock"
3. Add example responses
4. Create

## Defining Mock Responses

### Example-Based Mocking

**Request:** GET /users/123
**Example Response:**
\`\`\`json
{
  "id": 123,
  "name": "Jane Doe",
  "email": "jane@example.com",
  "role": "admin"
}
\`\`\`

**Status:** 200 OK

### Multiple Examples for Same Endpoint

**GET /users/123 - Happy Path:**
- Response: 200 with user object

**GET /users/123 - Not Found:**
- Response: 404 with error message
- Example name: "User not found"

**GET /users/123 - Unauthorized:**
- Response: 401 with error
- Example name: "Missing auth"

### Dynamic Mock Responses

Using Postman variables in mocks:

\`\`\`json
{
  "id": "{{$randomInt}}",
  "name": "{{$randomFirstName}} {{$randomLastName}}",
  "email": "{{$randomEmail}}",
  "created_at": "{{$timestamp}}",
  "request_id": "{{$guid}}"
}
\`\`\`

## Mock Server URL Structure

After creation, you get a URL like:
\`https://mock-api.postmanmock.com/v1/collections/abc123-def456\`

**Full API endpoint:**
\`https://mock-api.postmanmock.com/v1/collections/abc123-def456/users/123\`

## Advanced Mock Features

### 1. **Conditional Responses**

**Match by request body:**
\`\`\`json
// Request body
{
  "email": "admin@example.com"
}

// Response returns admin role
{
  "role": "admin"
}

// Different request body
{
  "email": "user@example.com"  
}

// Response returns user role
{
  "role": "user"
}
\`\`\`

### 2. **Query Parameter Matching**

**Request:** GET /products?category=electronics

**Response:**
\`\`\`json
{
  "products": [
    {"id": 1, "name": "Laptop", "category": "electronics"}
  ]
}
\`\`\`

### 3. **Response Delays**

Simulate network latency:

- **Default:** No delay
- **Slow network:** 2000ms delay
- **Timeout:** 10000ms delay

**Configuration:**
\`\`\`json
{
  "responseDelay": {
    "delayType": "fixed",
    "value": 2000
  }
}
\`\`\`

### 4. **HTTP Status Code Variations**

Test different scenarios:

\`\`\`
200 OK - Success
201 Created - Resource created
400 Bad Request - Invalid input
401 Unauthorized - Missing auth
403 Forbidden - No permission
404 Not Found - Missing resource
429 Too Many Requests - Rate limited
500 Internal Error - Server error
\`\`\`

## Practical Mock Examples

### Example 1: REST API for Blog

**Collection Structure:**
\`\`\`
📁 Blog API Mock
  ├── GET /posts
  │   ├── Example: List posts (200)
  │   └── Example: Empty list (200)
  ├── GET /posts/:id
  │   ├── Example: Found (200)
  │   ├── Example: Not found (404)
  │   └── Example: Invalid ID (400)
  ├── POST /posts
  │   ├── Example: Created (201)
  │   ├── Example: Validation error (400)
  │   └── Example: Unauthorized (401)
  └── DELETE /posts/:id
      ├── Example: Deleted (204)
      └── Example: Not found (404)
\`\`\`

### Example 2: Authentication Flow

**POST /auth/login - Success:**
\`\`\`json
// Response (200)
{
  "token": "eyJhbGciOiJIUzI1NiIs...",
  "user": {
    "id": 1,
    "name": "John Doe",
    "email": "john@example.com"
  }
}
\`\`\`

**POST /auth/login - Failure:**
\`\`\`json
// Response (401)
{
  "error": "Invalid credentials",
  "code": "AUTH_001"
}
\`\`\`

### Example 3: Paginated Response

**GET /products?page=1&limit=10:**
\`\`\`json
{
  "data": [...],
  "pagination": {
    "current_page": 1,
    "per_page": 10,
    "total": 100,
    "total_pages": 10,
    "next_url": "/products?page=2&limit=10",
    "prev_url": null
  }
}
\`\`\`

## Using Mock Servers in Development

### Frontend Integration

\`\`\`javascript
// Environment configuration
const API_URL = process.env.NODE_ENV === 'development'
  ? 'https://mock-api.postmanmock.com/v1/collections/abc123'
  : 'https://api.production.com';

// Fetch users from mock
fetch('\${API_URL}/users')
  .then(res => res.json())
  .then(data => console.log(data));
\`\`\`

### Contract Testing with Mocks

\`\`\`javascript
// Test that frontend works with mock
pm.test("Mock returns expected structure", () => {
    let jsonData = pm.response.json();
    
    // Verify contract
    pm.expect(jsonData).to.have.property('id');
    pm.expect(jsonData).to.have.property('name');
    pm.expect(jsonData).to.have.property('email');
    
    // Verify data types
    pm.expect(jsonData.id).to.be.a('number');
    pm.expect(jsonData.name).to.be.a('string');
    pm.expect(jsonData.email).to.match(/^[^@]+@[^@]+$/);
});
\`\`\`

## Mock Server Management

### Viewing Mock Server Activity

1. Go to Mock Server dashboard
2. View:
   - Request count
   - Response times
   - Most used endpoints
   - Recent requests

### Updating Mock Responses

1. Edit collection examples
2. Changes sync automatically
3. No need to restart mock server

### Deleting Mock Server

1. Go to Mock Servers list
2. Click "..." → Delete
3. Confirm deletion

## Best Practices

### ✅ **DO:**
- Create realistic mock responses
- Include error scenarios in mocks
- Use environment variables in mocks
- Document mock behavior
- Version control mock definitions
- Test mocks before frontend use
- Include response delays for realism

### ❌ **DON'T:**
- Don't use mocks for performance testing
- Don't hardcode sensitive data in mocks
- Don't forget to update mocks when API changes
- Don't use mocks in production
- Don't create overly complex mock logic

## Hands-On Exercises

### Beginner Exercise:
1. Create mock server for TODO API
2. Define GET /todos response
3. Define POST /todos response
4. Test mock in Postman

### Intermediate Exercise:
1. Create mock for e-commerce API
2. Include pagination in list endpoint
3. Add error scenarios (404, 401, 400)
4. Use dynamic variables for realistic data
5. Share mock URL with team

### Advanced Exercise:
1. Build frontend app connecting to mock
2. Implement contract tests
3. Simulate different response delays
4. Create test suite for all mock scenarios
5. Migrate from mock to real API

<div style="padding: 15px; background-color: #F0FDF4; border-left: 4px solid #22C55E; margin: 20px 0; border-radius: 4px;">
  <strong>💡 Pro Tip:</strong> Use mock servers for API design-first development. Design your API contract in Postman, share the mock URL, and let frontend and backend teams work simultaneously!
</div>

Now you can unblock your frontend team with mock servers! 🎭
        `,
        exercise: {
          title: "Build a Complete Mock API",
          description:
            "Create a mock server for a social media API with users, posts, comments, and likes.",
          starterCode: `// Requirements:
// 1. CRUD operations for users and posts
// 2. Authentication endpoints
// 3. Error scenarios for each endpoint
// 4. Pagination and filtering
// 5. Dynamic data generation
// Share mock URL with classmates`,
        },
      },
      {
        id: "monitors",
        title: "Monitors: Scheduled API Testing",
        estimatedMinutes: 12,
        content: `
# Monitors: 24/7 API Health Checks

Monitors automatically run collections on a schedule, alerting you when tests fail. They're your API's 24/7 watchdog.

## What Are Monitors?

Monitors are scheduled collection runs that:
- Run at specified intervals (hourly, daily, weekly)
- Execute all tests in a collection
- Send alerts on test failures
- Track API performance over time
- Provide historical run data

**Use Cases:**
- Production API health checks
- SLA monitoring (uptime, response time)
- Regression testing after deployments
- External dependency monitoring
- Performance trend analysis

## Creating a Monitor

### Prerequisites:
- Postman account (Free tier: 1000 monitor runs/month)
- Collection with tests
- Environment (optional)

### Steps:

1. **Create collection with tests**
   - Include assertions for all critical endpoints
   - Add performance checks (response time)

2. **Navigate to Monitors**
   - Click "Monitors" in left sidebar
   - Click "Create a Monitor"

3. **Configure Monitor**

**Basic Settings:**
\`\`\`
Name: Production API Health Check
Collection: API Tests Collection
Environment: Production Environment
\`\`\`

**Schedule:**
\`\`\`
Frequency: Every 1 hour
Start time: Now
Timezone: UTC
\`\`\`

**Advanced Options:**
\`\`\`
Delay between requests: 1000ms
Enable data persistence: Yes
Don't share run data: No
\`\`\`

4. **Configure Alerts**

**Email alerts:**
- On first failure
- On subsequent failures (after 5 minutes)

**Webhook alerts:**
- Send to Slack, Teams, or custom endpoint

## Monitor Configuration Options

### Schedule Frequencies

| Frequency | When to Use |
| :--- | :--- |
| Every 5 minutes | Critical production APIs |
| Every hour | Standard monitoring |
| Every 6 hours | Internal APIs |
| Daily | Overnight regression tests |
| Weekly | Full test suites |

### Regional Run Locations

Choose where monitors run from:
- **US East** (N. Virginia)
- **US West** (Oregon)  
- **EU West** (Ireland)
- **AP South** (Singapore)
- **Multiple regions** (Enterprise)

### Performance Baselines

Set expectations:
\`\`\`javascript
// In collection tests
pm.test("Response time < 500ms", () => {
    pm.expect(pm.response.responseTime).to.be.below(500);
});

pm.test("Response time within baseline", () => {
    let baseline = pm.environment.get("baseline_time") || 300;
    let variance = 0.2; // 20% variance allowed
    let maxAllowed = baseline * (1 + variance);
    pm.expect(pm.response.responseTime).to.be.below(maxAllowed);
});
\`\`\`

## Monitor Run Results

### Dashboard View

After monitor runs, you see:
- **Run Status:** Passed/Failed
- **Duration:** Total run time
- **Pass Rate:** Percentage of passed tests
- **Response Times:** Average/per endpoint

### Detailed Results

Click any monitor run to see:
- Console logs
- Request/response details
- Failed test assertions
- Environment snapshot

### Historical Data

Monitor keeps history of:
- Last 30 days of runs (Free)
- Unlimited history (Paid)
- Trends: uptime %, average response time

## Alerts and Notifications

### Email Alerts

Configure email recipients:
1. Monitor settings → Alerts
2. Add email addresses
3. Choose alert conditions:
   - Any failure
   - Consecutive failures (≥2)
   - Recovery notification

### Webhook Alerts

**Slack Integration:**
\`\`\`json
{
  "text": "🚨 *API Monitor Failed!*",
  "blocks": [
    {
      "type": "section",
      "text": {
        "type": "mrkdwn",
        "text": "*Monitor:* Production API Health Check\\n*Status:* Failed\\n*Failed Tests:* 3/15\\n*Response Time:* 1245ms"
      }
    }
  ]
}
\`\`\`

**Custom Webhook:**
1. Get webhook URL from your system
2. Configure in Postman:
   - URL: \`https://your-system.com/alerts\`
   - Headers: \`Content-Type: application/json\`
3. Postman sends alert payload

### PagerDuty Integration

For critical failures:
1. Connect Postman to PagerDuty
2. Configure escalation policies
3. Set up on-call schedules

## Best Practices for Monitors

### Monitor Collection Design

**✅ Good Monitor Collection:**
\`\`\`
📁 Production Health Check
  ├── 📁 Critical Paths
  │   ├── GET Health Check (always passes)
  │   ├── POST Login (auth test)
  │   └── GET User Profile (core functionality)
  ├── 📁 Business Logic
  │   ├── GET Products (main feature)
  │   ├── POST Order (transactional)
  │   └── GET Order Status (user flow)
  └── 📁 Performance
      ├── GET All Endpoints (response time)
      └── POST Heavy Query (resource usage)
\`\`\`

**❌ Bad Monitor Collection:**
- Long-running tests (>60 seconds)
- Destructive operations (DELETE, UPDATE)
- Tests without assertions
- Hardcoded environment values
- External dependencies that fail often

### Performance Optimization

**Keep monitors fast:**
\`\`\`javascript
// Set reasonable timeouts
pm.request.timeout = 10000; // 10 seconds

// Skip unnecessary waits
pm.environment.set("skip_delay", true);

// Cache repeated data
let token = pm.environment.get("cached_token");
if (token && token_expiry > Date.now()) {
    // Use cached token
} else {
    // Get new token
}
\`\`\`

### Data Management

**Avoid sensitive data:**
- Use environment variables for secrets
- Mark variables as "secret" in Postman
- Don't log sensitive data in tests

**Manage test data:**
\`\`\`javascript
// Clean up after tests
pm.test("Delete test user", () => {
    let testUserId = pm.environment.get("test_user_id");
    if (testUserId) {
        // Delete in tear-down request
        pm.sendRequest({
            url: \`https://api.example.com/users/\${testUserId}\`,
            method: 'DELETE'
        });
    }
});
\`\`\`

## Advanced Monitor Techniques

### 1. **Multi-Region Monitoring**

Run monitors from different regions to detect regional issues:

**Monitor 1:** US East
**Monitor 2:** EU West  
**Monitor 3:** AP Southeast

Compare results:
\`\`\`javascript
// Check consistency across regions
pm.test("Global response consistency", () => {
    let usTime = pm.environment.get("us_response_time");
    let euTime = pm.environment.get("eu_response_time");
    let apTime = pm.environment.get("ap_response_time");
    
    let variation = Math.max(usTime, euTime, apTime) - 
                    Math.min(usTime, euTime, apTime);
    
    pm.expect(variation).to.be.below(2000); // Within 2 seconds
});
\`\`\`

### 2. **Gradual Alert Escalation**

\`\`\`javascript
// Track consecutive failures
let failures = pm.environment.get("consecutive_failures") || 0;

pm.test("Critical endpoint", () => {
    if (pm.response.code !== 200) {
        failures++;
        pm.environment.set("consecutive_failures", failures);
        
        if (failures >= 3) {
            // Send high-priority alert
            console.error("CRITICAL: 3 consecutive failures!");
        }
    } else {
        // Reset counter on success
        pm.environment.set("consecutive_failures", 0);
    }
});
\`\`\`

### 3. **SLA Monitoring**

\`\`\`javascript
// Track against SLAs
const SLA = {
    uptime: 99.9, // percentage
    p95_response_time: 1000, // milliseconds
    error_rate: 0.01 // 1% max
};

let total_requests = pm.environment.get("total_requests") || 0;
let failed_requests = pm.environment.get("failed_requests") || 0;

pm.test("Uptime SLA", () => {
    total_requests++;
    if (pm.response.code >= 400) failed_requests++;
    
    let uptime = ((total_requests - failed_requests) / total_requests) * 100;
    pm.expect(uptime).to.be.at.least(SLA.uptime);
});
\`\`\`

## Monitor vs Collection Runner

| Feature | Monitor | Collection Runner |
| :--- | :--- | :--- |
| **Purpose** | Scheduled monitoring | Manual/ad-hoc testing |
| **Frequency** | Automatic | Manual trigger |
| **Alerts** | Yes (email/webhook) | No |
| **History** | Stored permanently | Temporary |
| **Data retention** | 30-90 days | Not stored |
| **Cost** | Monitor runs count toward limit | Free |
| **Location options** | Multiple regions | Local machine |

## Hands-On Exercises

### Beginner Exercise:
1. Create collection for health check API
2. Add tests for status and response time
3. Create monitor running hourly
4. Configure email alerts
5. View monitor results

### Intermediate Exercise:
1. Set up Slack webhook integration
2. Create multi-endpoint monitor
3. Add performance baseline tests
4. Configure conditional alerts (only if multiple failures)
5. Analyze historical trends

### Advanced Exercise:
1. Implement multi-region monitoring
2. Create SLA tracking in monitors
3. Set up PagerDuty integration
4. Build custom webhook receiver
5. Generate monthly reliability reports

<div style="padding: 15px; background-color: #F0FDF4; border-left: 4px solid #22C55E; margin: 20px 0; border-radius: 4px;">
  <strong>💡 Pro Tip:</strong> Start with critical endpoints only in your first monitor. Every endpoint you add increases run time and uses monitor runs quota. Focus on what truly matters for your API health!
</div>

Now you can sleep soundly knowing Postman monitors your APIs 24/7! 🛡️
        `,
        exercise: {
          title: "Production Monitoring Setup",
          description:
            "Set up comprehensive monitoring for a production API with alerting and reporting.",
          starterCode: `// Monitor requirements:
// 1. 15+ critical endpoints tested
// 2. Hourly schedule
// 3. Email + Slack alerts
// 4. Performance SLAs enforced
// 5. Regional variation tracking
// 6. Monthly reliability report generation`,
        },
      },
      {
        id: "workflows-integrations",
        title: "Advanced Workflows & Integrations",
        estimatedMinutes: 15,
        content: `
# Advanced Workflows & Integrations

Postman isn't just for testing—it's a platform for building complex API workflows and integrating with your entire development ecosystem.

## Advanced Scripting Patterns

### 1. **Conditional Workflows**

\`\`\`javascript
// Skip tests based on conditions
const environment = pm.environment.get("environment");

if (environment === "production") {
    pm.test("Production specific checks", () => {
        // Stricter security checks
        pm.response.to.have.header("Strict-Transport-Security");
        pm.response.to.have.header("Content-Security-Policy");
    });
} else {
    console.log("Skipping security headers check in non-prod");
}
\`\`\`

### 2. **Retry Logic for Flaky Tests**

\`\`\`javascript
// Automatic retry on failure
const maxRetries = 3;
let retryCount = pm.environment.get("retry_count") || 0;

pm.test("Flaky endpoint test", function() {
    if (pm.response.code === 500 && retryCount < maxRetries) {
        retryCount++;
        pm.environment.set("retry_count", retryCount);
        console.log(\`Retry \${retryCount} of \${maxRetries}\`);
        postman.setNextRequest(pm.info.requestName); // Repeat this request
    } else {
        pm.expect(pm.response.code).to.be.oneOf([200, 201]);
        pm.environment.set("retry_count", 0); // Reset on success
    }
});
\`\`\`

### 3. **Pagination Handling**

\`\`\`javascript
// Automatically fetch all pages
const currentPage = pm.environment.get("current_page") || 1;
const totalPages = pm.response.json().total_pages;

// Store data from this page
let allData = JSON.parse(pm.environment.get("all_data") || "[]");
allData.push(...pm.response.json().data);
pm.environment.set("all_data", JSON.stringify(allData));

// Get next page if more exist
if (currentPage < totalPages) {
    pm.environment.set("current_page", currentPage + 1);
    postman.setNextRequest("Get Page " + (currentPage + 1));
} else {
    console.log(\`Collected \${allData.length} total records\`);
    // Process complete dataset
    pm.environment.set("complete_data", JSON.stringify(allData));
    pm.environment.set("current_page", null);
}
\`\`\`

### 4. **Data Processing Between Requests**

\`\`\`javascript
// Transform data for next request
let users = pm.response.json().data;

// Filter active users
let activeUsers = users.filter(u => u.status === "active");

// Take first 5 for processing
let sampleUsers = activeUsers.slice(0, 5);

// Store for next request
pm.environment.set("user_ids", JSON.stringify(
    sampleUsers.map(u => u.id)
));

// Use in next request's body
let nextRequest = {
    url: "https://api.example.com/batch-process",
    method: "POST",
    body: {
        mode: "raw",
        raw: JSON.stringify({
            user_ids: sampleUsers.map(u => u.id),
            action: "send_notification"
        })
    }
};
\`\`\`

## External Integrations

### 1. **Webhook Receiver**

Set up Postman to receive webhooks:

**Create webhook receiver:**
1. Postman → Integrations → Webhook
2. Generate unique URL
3. Configure response

**Example webhook handler:**
\`\`\`javascript
// In collection pre-request script
pm.request.headers.add({
    key: "X-Webhook-Secret",
    value: pm.environment.get("webhook_secret")
});

// Test webhook payload
pm.test("Webhook received", () => {
    let payload = pm.request.body;
    pm.expect(payload.event_type).to.equal("user.created");
    
    // Store webhook data
    pm.environment.set("last_webhook_user", payload.user.id);
});
\`\`\`

### 2. **GitHub Integration**

**Actions:**
- Create issues on test failures
- Comment on PRs with test results
- Trigger tests on push

**Setup:**
1. Postman → Integrations → GitHub
2. Authenticate with GitHub
3. Configure events

**Test reporting to PR:**
\`\`\`javascript
// After collection run
const results = {
    passed: pm.run.stats.assertions.passed,
    failed: pm.run.stats.assertions.failed,
    time: pm.run.stats.responseTime.avg
};

// Use GitHub API to comment
pm.sendRequest({
    url: \`https://api.github.com/repos/owner/repo/issues/\${pr_number}/comments\`,
    method: "POST",
    header: {
        "Authorization": \`Bearer \${pm.environment.get("github_token")}\`
    },
    body: {
        mode: "raw",
        raw: JSON.stringify({
            body: \`API Tests: ✅ \${results.passed} passed, ❌ \${results.failed} failed in \${results.time}ms\`
        })
    }
});
\`\`\`

### 3. **Jenkins Integration**

**Trigger Postman from Jenkins:**

\`\`\`groovy
pipeline {
    agent any
    stages {
        stage('API Tests') {
            steps {
                script {
                    docker.image('postman/newman:latest').inside {
                        sh '''
                            newman run collection.json \
                                -e environment.json \
                                -r junit \
                                --reporter-junit-export results.xml
                        '''
                    }
                }
            }
        }
    }
}
\`\`\`

**Trigger Jenkins from Postman:**
\`\`\`javascript
// In Postman test script
pm.test("Trigger CI on failure", () => {
    if (pm.response.code !== 200) {
        pm.sendRequest({
            url: "https://jenkins.example.com/job/api-tests/build",
            method: "POST",
            header: {
                "Authorization": \`Bearer \${pm.environment.get("jenkins_token")}\`
            }
        });
    }
});
\`\`\`

### 4. **Slack/Teams Integration**

**Send formatted messages:**

\`\`\`javascript
// Slack notification
const slackMessage = {
    blocks: [
        {
            type: "header",
            text: {
                type: "plain_text",
                text: pm.response.code === 200 ? "✅ API Test Passed" : "❌ API Test Failed"
            }
        },
        {
            type: "section",
            fields: [
                { type: "mrkdwn", text: "*Endpoint:*\n" + pm.request.url },
                { type: "mrkdwn", text: "*Status:*\n" + pm.response.code },
                { type: "mrkdwn", text: "*Time:*\n" + pm.response.responseTime + "ms" },
                { type: "mrkdwn", text: "*Environment:*\n" + pm.environment.get("env") }
            ]
        }
    ]
};

pm.sendRequest({
    url: pm.environment.get("slack_webhook"),
    method: "POST",
    body: {
        mode: "raw",
        raw: JSON.stringify(slackMessage)
    }
});
\`\`\`

## Building Complex Workflows

### 1. **E-Commerce Checkout Flow**

\`\`\`
Collection: Checkout Workflow
  ├── 1. POST /auth/login (get token)
  ├── 2. GET /products (browse)
  ├── 3. POST /cart/add (add items)
  ├── 4. GET /cart (review)
  ├── 5. POST /checkout (process)
  ├── 6. GET /order/:id (confirm)
  └── 7. (Conditional) POST /order/cancel (if price > threshold)
\`\`\`

**Flow logic:**
\`\`\`javascript
// After checkout
const totalPrice = pm.response.json().total;
pm.environment.set("order_total", totalPrice);

// Conditional cancellation
if (totalPrice > 1000) {
    console.log("Expensive order, testing cancellation");
    postman.setNextRequest("Cancel Order");
} else {
    console.log("Normal order, completing");
    postman.setNextRequest("Confirm Order");
}
\`\`\`

### 2. **Data Seeding for Tests**

\`\`\`javascript
// Setup script runs before all tests
const testData = {
    users: [
        { name: "Test User 1", email: "test1@example.com" },
        { name: "Test User 2", email: "test2@example.com" }
    ],
    products: [
        { name: "Test Product", price: 99.99 }
    ]
};

// Create test users
testData.users.forEach(user => {
    pm.sendRequest({
        url: pm.environment.get("api_url") + "/users",
        method: "POST",
        body: { mode: "raw", raw: JSON.stringify(user) }
    }, (err, res) => {
        if (!err) {
            let userIds = JSON.parse(pm.environment.get("test_user_ids") || "[]");
            userIds.push(res.json().id);
            pm.environment.set("test_user_ids", JSON.stringify(userIds));
        }
    });
});
\`\`\`

### 3. **Performance Testing Workflow**

\`\`\`javascript
// Load test multiple endpoints
const endpoints = [
    "/users", "/products", "/orders", "/search"
];

let results = [];

endpoints.forEach(endpoint => {
    let startTime = Date.now();
    
    pm.sendRequest({
        url: pm.environment.get("api_url") + endpoint,
        method: "GET"
    }, (err, res) => {
        let duration = Date.now() - startTime;
        results.push({ endpoint, duration, status: res.code });
        
        if (results.length === endpoints.length) {
            // All tests complete
            console.table(results);
            
            // Check performance
            let slowEndpoints = results.filter(r => r.duration > 1000);
            if (slowEndpoints.length > 0) {
                console.warn("Slow endpoints:", slowEndpoints);
                // Trigger alert
            }
        }
    });
});
\`\`\`

## API Gateway Integration

### Kong Gateway Testing

\`\`\`javascript
// Test rate limiting
for (let i = 1; i <= 110; i++) {
    pm.sendRequest({
        url: "https://api.example.com/limited-endpoint",
        method: "GET"
    }, (err, res) => {
        if (res.code === 429) {
            console.log(\`Rate limited after \${i} requests\`);
            pm.test("Rate limit works", () => {
                pm.expect(res.headers.get("X-RateLimit-Reset")).to.exist;
            });
        }
    });
}
\`\`\`

### AWS API Gateway Testing

\`\`\`javascript
// Test API Gateway features
pm.test("API Keys work", () => {
    pm.response.to.have.header("x-amz-apigw-id");
    pm.response.to.have.header("x-amzn-RequestId");
});

pm.test("Custom domain works", () => {
    pm.expect(pm.request.url.host).to.include("api.custom-domain.com");
});
\`\`\`

## Postman API (Programmatic Access)

### Automating Postman Itself

\`\`\`bash
# Create collection via API
curl -X POST https://api.getpostman.com/collections \
  -H "X-Api-Key: YOUR_API_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "collection": {
      "info": { "name": "Auto-Generated Collection" },
      "item": []
    }
  }'
\`\`\`

### Update Collection from CI

\`\`\`javascript
// node script to update collection
const PostmanAPI = require('postman-api');

const api = new PostmanAPI({
    apiKey: process.env.POSTMAN_API_KEY
});

// Update collection with new tests
api.updateCollection(collectionId, {
    item: [
        {
            name: "New Test",
            request: {
                method: "GET",
                url: "https://api.example.com/test"
            }
        }
    ]
});
\`\`\`

## Best Practices

### ✅ **DO:**
- Use Postman API for automation
- Integrate with your CI/CD pipeline
- Send alerts to team communication channels
- Implement retry logic for flaky tests
- Use environment variables for all config
- Document complex workflows
- Test integrations in isolation first

### ❌ **DON'T:**
- Don't create infinite loops in workflows
- Don't hardcode integration URLs
- Don't ignore webhook signatures (security!)
- Don't overcomplicate simple tests
- Don't forget error handling in workflows

## Hands-On Exercises

### Beginner Exercise:
1. Set up Slack webhook notification
2. Create conditional workflow (skip some tests)
3. Implement simple pagination handler
4. Test GitHub integration

### Intermediate Exercise:
1. Build multi-step order processing workflow
2. Add retry logic for external API calls
3. Implement data transformation between requests
4. Create Jenkins pipeline with Postman

### Advanced Exercise:
1. Build complete e-commerce checkout flow
2. Add performance monitoring and alerting
3. Implement complex data seeding/cleanup
4. Create self-healing tests (auto-fix common issues)
5. Build dashboard using Postman API data

<div style="padding: 15px; background-color: #F0FDF4; border-left: 4px solid #22C55E; margin: 20px 0; border-radius: 4px;">
  <strong>💡 Pro Tip:</strong> Use Postman's built-in \`pm.sendRequest\` for complex workflows that need to make additional API calls during test execution—perfect for setup, cleanup, or conditional branching!
</div>

Now you can build sophisticated API workflows and integrate Postman with your entire toolchain! 🔗
        `,
      },
    ],
  },
];
